/**
 * VehiclePositionMap.tsx
 * 車両ポジションマップ — ビジュアル車両位置管理
 * ドラッグ＆ドロップで車両ステータスを変更
 */
import { isSpotStaff } from '../utils/staffPriority';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Typography, Paper, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, CircularProgress, TextField, useTheme, useMediaQuery } from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  useDndContext,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { updateVehicle } from '../api/vehicleService';
import { saveDispatchReservation } from '../api/dispatchService';
import { vehicleSyncService } from '../services/vehicleSyncService';
import { reservationSyncService } from '../services/reservationSyncService';
import { useReservationData } from '../context/ReservationDataContext';
import { UI } from '../styles/uiTokens';
import { shortVehicleCode } from '../lib/displayFormat';
import { getVehicleZonesConfig } from '../config/vehicleZones';
import { rowPrefixOf, rowPrefixTitleOf, hotelDeliveryMarkOf, cruiseMarkOf } from '../utils/planPrefix';
import {
  ARRIVAL_CONTACT_KEY, AIRPORT_KEY, ARRIVAL_CONTACT_ON, AIRPORT_WALKIN, AIRPORT_CALLED,
  PREPARE_AIRPORT_VALUES, SHUTTLE_AIRPORT_VALUES,
  getArrivalContact, isPreparingAtShop, needsShuttleDriver,
} from '../lib/arrivalContact';
import {
  AIRPORT_SHUTTLE, getAltShuttleNumber, normalizeAltShuttleNumber, stripStaleAltShuttle,
  transportLabel, withShuttleContact, withoutShuttleContact,
} from '../lib/transportVehicle';
import type { ZoneConfig } from '../config/vehicleZones';

// --- 保存 + 同期キャッシュ即時反映ラッパー ---
// 保存成功レスポンス（サーバ確定値）を sync キャッシュへ即時マージし、
// ポーリング（30秒）/ Pusher を待たずに全画面へ反映する。
// Pusher 無効の宮古で「楽観的更新（5秒）解除後にカードが元のゾーンへ戻る」問題の修正。
const saveVehicleAndSync = async (payload: any, userId?: string | number): Promise<any> => {
  const updated = await updateVehicle(payload, userId);
  if (updated) vehicleSyncService.applyLocalUpdate(updated);
  return updated;
};
/**
 * 出発時の車両チェック初期化（2026-09-05）。
 *
 * 出発＝お客様が乗って出た＝次のサイクルの始まりなので、掃除・洗車・VC 等の
 * チェックを落とす。貸出返却リストの「チェックを初期化しますか？」や
 * リクエストウィンドウの当日貸出と同じ扱いにする。
 * ※ 車両ポジションの DnD だけこれが抜けていて「出発中なのに掃除が済んだまま」に
 *   なっていた（現場報告 2026-09-05）。
 *
 * check_situations（新形式）があればそれを value:false にする。無い車両だけ
 * 旧チェックフィールドを false にする。useStatusDialogs.executeStatusUpdate と同じ方針。
 */
const CHECK_FIELDS = ['cleaning', 'car_wash', 'check1', 'check2', 'tire_groove', 'tire_pressure'];

const buildClearedChecks = (vehicle: any): Record<string, any> => {
  if (Array.isArray(vehicle?.check_situations) && vehicle.check_situations.length > 0) {
    return { check_situations: vehicle.check_situations.map((item: any) => ({ ...item, value: false })) };
  }
  const out: Record<string, any> = {};
  CHECK_FIELDS.forEach(f => { out[f] = false; });
  return out;
};

/** 必須チェックのうち1つでも完了しているか（＝初期化の必要があるか） */
const hasCompletedChecks = (vehicle: any): boolean => {
  if (Array.isArray(vehicle?.check_situations)) {
    return vehicle.check_situations.some((c: any) => c?.value === true);
  }
  return CHECK_FIELDS.some(f => vehicle?.[f] === true);
};

const saveReservationAndSync = async (input: any): Promise<any> => {
  // 空港を送迎車以外にしたら別車両番号も消す（DnD のどの経路でも古い番号を残さない）
  const saved = await saveDispatchReservation(stripStaleAltShuttle(input));
  if (saved) reservationSyncService.applyLocalUpdate(saved);
  return saved;
};

// --- 点滅アニメーション用CSS ---
const pulseKeyframes = `
@keyframes vehiclePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
/* 貸出返却リストの「⬇」から飛んで来たときに対象カードを目立たせる（2026-08-10）。
   外側への box-shadow はゾーンのスクロール領域に左右を切られて上下しか見えないため、
   **カード内側のリング**を opacity で明滅させる */
@keyframes vehicleLocateGlow {
  0%, 100% { opacity: 0.15; }
  50% { opacity: 1; }
}
/* 送迎車/返却車でお客様を運ぶ間、店舗で待つアサイン車両を黄色のリングで点滅させる（2026-09-19）。
   貸出返却リストの対応中（黄色点滅）と同じ意味。移動中の opacity 点滅と区別する */
@keyframes vehiclePrepareGlow {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
}`;
if (typeof document !== 'undefined' && !document.getElementById('vehicle-pulse-style')) {
  const style = document.createElement('style');
  style.id = 'vehicle-pulse-style';
  style.textContent = pulseKeyframes;
  document.head.appendChild(style);
}

// --- ゾーン定義 ---
// statusCode は API `/api/statuses` の vehicle.code（status_code）と一致させる。
// 実行時 vehicleStatusList から id を解決するので、DBの id 採番が変わってもコード変更は不要。
// ゾーン構成は店舗（那覇/宮古）の物理レイアウトに依存するため config/vehicleZones.ts で
// インスタンス別に定義し、ここでは現在店舗の設定を取得して使う。
const ZONES_CONFIG = getVehicleZonesConfig();
const ZONES: ZoneConfig[] = ZONES_CONFIG.zones;
const MOBILE_ZONE_ORDER = ZONES_CONFIG.mobileZoneOrder;
const ALWAYS_SHOW_ZONE_IDS = ZONES_CONFIG.alwaysShowZoneIds;

// vehicleStatusList から statusCode→zone_id マッピングを生成する際の優先度。
// 同一 statusCode を持つ複数ゾーンがある場合、ここに含まれる ID を優先（droppable な実体ゾーンを採用）。
const PREFERRED_ZONE_BY_CODE: Record<string, string> = {
  p_u: 'pickup', // p_u は 'airport_1f' (droppable: false) と 'pickup' の両方にあるが、車両振り分け先は 'pickup'
};

// Hゾーン
const H_ZONE: ZoneConfig = {
  id: 'departed_today', label: '貸出', statusCode: 'departing', gridArea: 'h',
  color: '#ebedf7', borderColor: '#3949ab',
};

// ゾーンごとの移動ステータス code（ドロップ時にセット）。店舗別定義（config/vehicleZones.ts）。
// 同じ表を貸出返却リスト側も lib/vehicleZoneMove.ts 経由で参照している（2026-08-25）。
// 「どのゾーンへ移すと何が起きるか」を2箇所で書き分けないこと。
// id は店舗ごとに採番が違うので、実行時に locationStatusList から解決する（2026-09-01）
const ZONE_LOCATION_CODES: Record<string, string> = ZONES_CONFIG.zoneLocationStatus;

// Rゾーン（予約リスト、ドロップ不可）
const R_ZONE: ZoneConfig = {
  id: 'reservation_list', label: '予約リスト', statusCode: null, gridArea: 'r',
  color: '#e6f0ef', borderColor: '#00695c', droppable: false,
};

/**
 * 予約リスト内「連絡済み」サブエリアのドロップID（2026-08-10 追加）。
 * こちらから電話連絡した予約の目印。2026-10-19 から保存先を json_data「空港: 連絡済」に統合
 * （到着連絡は入れない。lib/arrivalContact.ts の AIRPORT_CALLED）。旧キー「電話連絡: 済」は読まない。
 */
const CALLED_DROP_ID = 'reservation_called';
/**
 * ゾーン本体のドロップ先（見出しの帯）。サブエリア・車両カードが増えて、ゾーン本体の空き地に落とすのが
 * 難しくなったため、見出しの行そのものを本体の受け皿にする（2026-10-21）。id は `${zoneId}${MAIN_DROP_SUFFIX}`
 */
const MAIN_DROP_SUFFIX = '::main';
/** 店舗ゾーン「出発準備」枠のドロップ id（2026-09-19。旧 airport_walkin） */
const PREPARE_DROP_ID = 'airport_prepare';
/**
 * 店舗ゾーン「出発案内」枠（2026-10-21）。出発準備の右。ピックアップ（空港1F P.U・赤嶺駅P.U・下地島P.U）から
 * 店舗へ動かした車両＝お客様を店舗にお連れして出発のご案内をする車両がここに並ぶ。枠へ直接落としても入る。
 * 正は**車両の json_data「出発案内: YYYY-MM-DD」**（入れた日。当日の日付のときだけ表示するので、
 * 別の画面から出発させて印が残っても翌日以降は出ない）。店舗の本体・出発準備・ほかのゾーンへ動かすと外す。
 */
const GUIDE_DROP_ID = 'airport_guide';
const GUIDE_JSON_KEY = '出発案内';
/** 旧キー（2026-10-19 まで）。予約リストへ戻すときに消すためだけに残す */
const LEGACY_CALLED_JSON_KEY = '電話連絡';

// 予約ステータス除外セット（cancelled, nonreserved, full, done）
const EXCLUDED_RES_STATUSES = new Set(['cancelled', 'nonreserved', 'full', 'done', '4', '5', '6', '2']);

// ドロップオフゾーン: 車両がここから非ドロップオフゾーンへ移動した場合、
// 紐づく返却予約を done(貸出済み) に変更するか確認モーダルを表示する。店舗別定義
const DROPOFF_ZONE_IDS = ZONES_CONFIG.dropoffZoneIds;

/**
 * 返却送迎（送迎車モード）。2026-08-24 追加。
 *
 * 返却客を空港でドロップオフした車が、そのまま別の予約客を迎えに行く運用。
 * ドロップオフ → ピックアップ へ車両を動かすと発動し、送迎する予約を一時的に
 * 紐づける。ピックアップ/ドロップオフ以外のゾーンへ動かすと解除される。
 *
 * モードの正は **車両の json_data.data['返却送迎']**（カンマ区切りの予約ID）。
 * 車両ステータスは「今どこにいるか」だけを表す従来どおりの意味を保つため、
 * 専用ステータス（返却迎/tmp_pu_bus）は使わない。赤嶺駅・下地島でも
 * ゾーン定義（pickupZoneIds/dropoffZoneIds）だけで同じ挙動になる。
 *
 * モード中はこの車両に「本来紐づく次の貸出予約」をカードに出さない。
 * 迎えに行く相手は無関係な予約なので、次の貸出予約を出すと誤解を招くため。
 */
const PICKUP_ZONE_IDS = ZONES_CONFIG.pickupZoneIds;
const SHUTTLE_ZONE_IDS = new Set<string>([...Array.from(PICKUP_ZONE_IDS), ...Array.from(DROPOFF_ZONE_IDS)]);
const SHUTTLE_JSON_KEY = '返却送迎';

/**
 * 貸出済みにする返却予約を探す（2026-10-19）。表示日の返却予定にこの車両の予約があればそれ。
 * 無ければ同じ車両で「利用中」の予約（早め・遅めの返却で返却日が表示日とずれた予約）。
 * 以前は表示日の返却予定だけを見ていたため、ずれた予約では確認モーダルが出ず利用中のまま残った。
 */
const findReturnToComplete = (returnsOfDay: any[], vid: string): any | undefined =>
  returnsOfDay.find((r: any) => String(r.vehicle_id) === vid)
  ?? (reservationSyncService.getReservations() as any[]).find((r: any) =>
    String(r.vehicle_id) === vid && (String(r.status) === '3' || r.status === 'in_use'));
const SHUTTLE_AIRPORT_VALUE = '返却車';
/** 空港1Fエリアに送迎車サブエリアがある店舗か（那覇・宮古とも有り。無い店舗ではボタンを出さない） */
const hasShuttleSubZone = ZONES_CONFIG.pickupSubZones.some(sz => sz.airportValue === AIRPORT_SHUTTLE);

/** 車両の json_data から返却送迎の予約IDリストを取り出す */
const getShuttleResIds = (vehicle: any): string[] => {
  const raw = (vehicle?.json_data as any)?.data?.[SHUTTLE_JSON_KEY];
  if (!raw) return [];
  return String(raw).split(',').map(v => v.trim()).filter(Boolean);
};

/**
 * 返却送迎を書き込んだ json_data を作る。
 * 車両 json_data は { data, types } のカスタムフィールド形式（車両詳細パネルが編集する）
 * なので、既存キーを壊さないよう data/types 双方をマージして返す。
 */
const buildShuttleJsonData = (vehicle: any, ids: string[]): any => {
  const jd = (vehicle?.json_data && typeof vehicle.json_data === 'object') ? vehicle.json_data : {};
  const data = { ...((jd as any).data || {}) };
  const types = { ...((jd as any).types || {}) };
  if (ids.length) {
    data[SHUTTLE_JSON_KEY] = ids.join(',');
    types[SHUTTLE_JSON_KEY] = 'text';
  } else {
    delete data[SHUTTLE_JSON_KEY];
    delete types[SHUTTLE_JSON_KEY];
  }
  return { ...(jd as any), data, types };
};

/** 車両の json_data に出発案内の印（当日の日付）を付け外しした json_data を作る（他のキーは保つ） */
const buildGuideJsonData = (baseJson: any, on: boolean): any => {
  const jd = (baseJson && typeof baseJson === 'object') ? baseJson : {};
  const data = { ...((jd as any).data || {}) };
  const types = { ...((jd as any).types || {}) };
  if (on) {
    data[GUIDE_JSON_KEY] = dayjs().format('YYYY-MM-DD');
    types[GUIDE_JSON_KEY] = 'text';
  } else {
    delete data[GUIDE_JSON_KEY];
    delete types[GUIDE_JSON_KEY];
  }
  return { ...(jd as any), data, types };
};
/** 車両の json_data の出発案内が今日の日付か */
const hasGuideToday = (vehicle: any): boolean =>
  String((vehicle?.json_data as any)?.data?.[GUIDE_JSON_KEY] ?? '') === dayjs().format('YYYY-MM-DD');

// --- Props ---
interface VehiclePositionMapProps {
  vehicles: Record<string, any>;
  vehicleStatusList: any[];
  locationStatusList?: any[];
  departures: any[];
  returns: any[];
  upcomingStatuses?: Record<string, any>;
  onVehicleClick?: (vehicle: any) => void;
  onStaffClick?: (reservation: any, staffType: string, listType: string) => void;
  onReservationClick?: (reservation: any) => void;
  /** 貸出返却リストの「⬇」で指定された車両ID（この車両までスクロール&グローさせる） */
  locateVehicleId?: string | null;
  /** 同じ車両を続けて押しても再発火させるためのカウンタ */
  locateNonce?: number;
  onOpenLineChat?: (lineUser: any, reservationId: string) => void;
  lineUsers?: any[];
  staffList?: any[];
  userId?: string;
}

// --- ピックアップサブゾーン共通定義（export for ReservationTable） ---
// 店舗別（那覇: 14番 / 宮古: 乗降場）。定義は config/vehicleZones.ts
export const PICKUP_SUB_ZONES = ZONES_CONFIG.pickupSubZones;

// --- 返却の受け方（返却リストの「連絡」用。2026-08-24）---
// 貸出のピックアップとは別概念・別の保存キー（返却連絡 / 返却場所）。
// 車両ポジションマップでは使わない（返却の配置は車両ステータスで表すため）
export const RETURN_SUB_ZONES = ZONES_CONFIG.returnSubZones;

// --- 予約情報の型 ---
/** 予約1件から取り出す表示メタ情報（貸出返却リストと同じ記号体系。2026-08-10 追加） */
interface ResMeta {
  planPrefix: string;   // 🏨(ホテル配車・優先) / 🚢(クルーズ船) / 🏪(駅近来店) / 👤(代表者送迎) / 🚶‍♀️(セルフ) / 🚃(ステーション) / ''
  /** ホテル配車・クルーズ船の説明（tooltip）と、配車場所が未入力か（2026-10-25・🚢 は 2026-11-02） */
  hotelLabel?: string;
  hotelPlaceMissing?: boolean;
  shortMemo: string;    // ⚠️ 表示用の短メモ
  agreement: boolean;   // 受
  license: boolean;     // 免
  payment: boolean;     // 支
  pkg: boolean;         // P（パッケージ）
  carmore: boolean;     // C（CarMore経由）
}

/** 予約から ResMeta を作る（ReservationTable と同じ判定ロジック） */
const buildResMeta = (r: any, rowType: 'departure' | 'return'): ResMeta => {
  const d = r?.detail || {};
  const contactPlan = String(r?.contact_plan || d.contact_plan || '');
  // 🏨 ホテル配車（貸出 / 返却で条件が違う。判定は utils/planPrefix.ts。マップのカードは 🏨 だけで枠は付けない）
  const hotel = hotelDeliveryMarkOf(r?.json_data || d.json_data, rowType);
  // 🚢 クルーズ船（2026-11-02。マップのカードも 🚢 だけで枠は付けない）
  const cruise = cruiseMarkOf(r?.json_data || d.json_data, r?.comment || d.comment);
  const media = String(d.contact_media || r?.contact_media || '');
  const planName = String(d.plan_name || r?.plan_name || '');
  return {
    planPrefix: rowPrefixOf(contactPlan, hotel, cruise),
    hotelLabel: (hotel || cruise) ? rowPrefixTitleOf(contactPlan, hotel, cruise) : undefined,
    hotelPlaceMissing: !!hotel?.placeMissing,
    shortMemo: String(r?.short_memo || ''),
    agreement: Number(d.agreement) === 1 || d.agreement === true,
    license: Number(d.license) === 1 || d.license === true,
    payment: Number(d.payment) === 1 || d.payment === true,
    pkg: media.toUpperCase().includes('PKG') || planName.toUpperCase().includes('PKG'),
    carmore: media.toLowerCase().includes('carmore'),
  };
};

interface VehicleResInfo {
  returnRes?: { time: string; customerName: string; reservationId?: string; isToday?: boolean };
  departureRes?: {
    time: string; customerName: string; reservationId?: string; isToday?: boolean;
    /** 「連絡」の空港サブゾーン値（送迎車 / 返却車 / 直接来店 …）。連絡 OFF は '' */
    airport?: string;
  };
  /** 返却送迎（送迎車モード）で一時的に紐づけた予約。ある場合 departureRes は出さない */
  shuttleRes?: { time: string; customerName: string; reservationId: string }[];
  /** この車両の貸出予約のお客様を運ぶ車（送迎車 / 別1234 / 返8118）。カードに出して点滅させる（2026-11-04） */
  transportTag?: string;
  /** 表示メタ（貸出予約を優先、無ければ返却予約） */
  meta?: ResMeta;
  driverName?: string;
  driverBgColor?: string;
  driverTextColor?: string;
}

/** 顧客名の省略ルール（貸出返却リストと揃える: 6文字超は先頭6文字+…） */
const truncCustomerName = (s: any): string => {
  const t = String(s ?? '');
  return t.length > 6 ? `${t.slice(0, 6)}…` : t;
};

/** 受/免/支 ミニチップ（ReservationTable の checkMini と同じ配色） */
const checkChip = (label: string, ok: boolean, tooltip: string, special?: 'pkg' | 'carmore') => (
  <span
    key={label}
    title={tooltip}
    style={{
      fontSize: '0.5rem', fontWeight: 700, padding: '0 3px', borderRadius: '2px', lineHeight: 1.5,
      backgroundColor: special === 'pkg' ? '#2563eb' : special === 'carmore' ? '#0d9488' : (ok ? '#16a34a' : '#fff'),
      color: (special === 'pkg' || special === 'carmore') ? '#fff' : (ok ? '#fff' : '#dc2626'),
      border: `1px solid ${special === 'pkg' ? '#1d4ed8' : special === 'carmore' ? '#0f766e' : (ok ? '#15803d' : '#fca5a5')}`,
      whiteSpace: 'nowrap',
    }}
  >{label}</span>
);

// --- VehicleCard ---
export function VehicleCard({ vehicle, resInfo, isDragging, locStatusMap, lineUserMap, onOpenLineChat, onToggleLocation }: {
  vehicle: any;
  resInfo?: VehicleResInfo | null;
  isDragging?: boolean;
  locStatusMap?: Record<number, { label: string; bg_color?: string; text_color?: string }>;
  lineUserMap?: Record<string, any>;
  onOpenLineChat?: (lineUser: any, reservationId: string) => void;
  /**
   * ピックアップ / ドロップオフの車両だけ渡す（2026-10-25）。移動ステータスの表示（空港← / →店舗）がボタンになり、
   * 押すと 行き ⇄ 戻り が切り替わる（カードのほかの場所のクリック＝詳細モーダルとは別）。
   * 以前はダブルクリックだったが、1回目のクリックで詳細モーダルが開き2回目がモーダルの外に当たって成立しなかった
   */
  onToggleLocation?: () => void;
}) {
  const bgColor = vehicle.vehicle_type?.bg_color || '#e0e0e0';
  const textColor = vehicle.vehicle_type?.text_color || '#000';
  const statusId = Number(vehicle.status);
  const locStatus = vehicle.location_status ? locStatusMap?.[vehicle.location_status] : null;
  // 利用中(1)/送り中(10) → 返却予約が優先（現在占有中）
  // 空港店(2)/迎え中(14)/ヤード(4) → 貸出予約が優先（次の利用者）
  const isReturnPrimary = statusId === 1 || statusId === 10;
  const primary = isReturnPrimary ? resInfo?.returnRes : resInfo?.departureRes;
  const secondary = isReturnPrimary ? resInfo?.departureRes : resInfo?.returnRes;
  const primaryLabel = isReturnPrimary ? '返却' : '貸出';
  const secondaryLabel = isReturnPrimary ? '貸出' : '返却';
  const meta = resInfo?.meta;
  // 返却送迎（送迎車モード）で一時的に紐づけた予約（2026-08-24）
  const shuttleRes = resInfo?.shuttleRes || [];

  return (
    <Paper
      elevation={isDragging ? 8 : 1}
      sx={{
        px: 0.5,
        pt: 0.2,
        pb: 0.2,
        borderRadius: 1,
        backgroundColor: bgColor,
        color: textColor,
        cursor: 'grab',
        minWidth: 95,
        maxWidth: 150,
        opacity: isDragging ? 0.8 : 1,
        transform: isDragging ? 'scale(1.05)' : 'none',
        transition: 'transform 0.1s, box-shadow 0.1s',
        userSelect: 'none',
        '&:hover': { boxShadow: 3 },
      }}
    >
      {/* 車両コード行: 車両番号 + 🚶‍♀️/⚠️（2026-08-10 追加） + LINEアイコン */}
      {/* 貸出返却リストと同じく末尾番号だけを表示する（2026-08-09。全文は title 属性で補う） */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Typography title={vehicle.vehicle_code} sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.2 }}>
            {shortVehicleCode(vehicle.vehicle_code)}
          </Typography>
          {meta?.planPrefix && (
            <span title={meta.hotelLabel || 'プラン'} style={{ fontSize: '0.7rem', lineHeight: 1, position: 'relative' }}>
              {meta.planPrefix}
              {meta.hotelPlaceMissing && (
                // 配車場所が未入力（⚠️ はショートメモの印なので赤い点）
                <span style={{ position: 'absolute', top: -2, right: -3, width: 6, height: 6, borderRadius: '50%', backgroundColor: '#dc2626', border: '1px solid #fff' }} />
              )}
            </span>
          )}
          {meta?.shortMemo && (
            <span title={meta.shortMemo} style={{ fontSize: '0.7rem', lineHeight: 1 }}>⚠️</span>
          )}
        </div>
        {(() => {
          const primaryResId = primary?.reservationId || secondary?.reservationId;
          const lineUser = primaryResId && lineUserMap ? lineUserMap[primaryResId] : null;
          if (!lineUser) return null;
          return (
            <span
              style={{
                fontSize: '0.7rem',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenLineChat && primaryResId) {
                  onOpenLineChat(lineUser, primaryResId);
                }
              }}
            >
              💬
            </span>
          );
        })()}
      </div>
      {/* 受 / 免 / 支 行: 車両番号の下（2026-08-10 追加。貸出返却リストと同じ記号・配色） */}
      {meta && (
        <div style={{ display: 'flex', gap: 2, marginTop: 1 }}>
          {checkChip('受', meta.agreement, `事前受付: ${meta.agreement ? '完了' : '未完了'}`)}
          {checkChip('免', meta.license, `免許証: ${meta.license ? '完了' : '未完了'}`)}
          {(!meta.payment && meta.carmore)
            ? checkChip('C', true, 'CarMore経由（支払い済み・確認不要）', 'carmore')
            : (!meta.payment && meta.pkg)
              ? checkChip('P', true, 'パッケージ', 'pkg')
              : checkChip('支', meta.payment, `支払い: ${meta.payment ? '完了' : '未完了'}`)}
        </div>
      )}
      {/* タグ行: 運転者 + 位置ステータス */}
      {(resInfo?.driverName || locStatus || onToggleLocation || resInfo?.transportTag) && (
        <div style={{ fontSize: '0.6rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, marginTop: 2, marginBottom: 2 }}>
          {resInfo?.driverName ? (
            <span style={{
              fontWeight: 600,
              backgroundColor: resInfo.driverBgColor || '#e8f5e9',
              color: resInfo.driverTextColor || '#2e7d32',
              border: `1px solid ${resInfo.driverTextColor || '#4caf50'}`,
              borderRadius: '3px',
              padding: '1px 4px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}>
              {resInfo.driverName.length > 6 ? resInfo.driverName.slice(0, 6) + '..' : resInfo.driverName}
            </span>
          ) : <span />}
          {resInfo?.transportTag && (
            <span
              data-testid="vehicle-transport-tag"
              title="お客様を運ぶ車"
              style={{
                fontWeight: 700,
                backgroundColor: resInfo.transportTag.startsWith('返') ? '#6d28d9' : '#e65100',
                color: '#fff',
                borderRadius: '3px',
                padding: '1px 3px',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {resInfo.transportTag}
            </span>
          )}
          {onToggleLocation ? (
            // 押せる移動ステータス（P.U / D.O）。ドラッグ開始とカードのクリック（詳細モーダル）に伝えない
            <button
              type="button"
              data-testid="location-toggle"
              title="押すと 空港へ向かい中 ⇄ 店舗へ戻り中 を切り替え"
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onToggleLocation(); }}
              onDoubleClick={(e) => e.stopPropagation()}
              style={{
                font: 'inherit', fontWeight: 600, cursor: 'pointer',
                backgroundColor: locStatus ? (locStatus.bg_color || '#666') : '#fff',
                color: locStatus ? (locStatus.text_color || '#fff') : '#94a3b8',
                border: locStatus ? '1px solid transparent' : '1px dashed #cbd5e1',
                borderRadius: '3px',
                padding: '1px 3px',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
              }}
            >
              {locStatus ? locStatus.label : '→空港'} ⇄
            </button>
          ) : locStatus && (
            <span style={{
              fontWeight: 600,
              backgroundColor: locStatus.bg_color || '#666',
              color: locStatus.text_color || '#fff',
              borderRadius: '3px',
              padding: '1px 3px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}>
              {locStatus.label}
            </span>
          )}
        </div>
      )}
      {/* 返却送迎（送迎車モード）: モード表示 + 迎えに行く予約（2026-08-24）。
          この車両本来の次回貸出予約は reservationInfoMap 側で落としてあるので出ない */}
      {shuttleRes.length > 0 && (
        <div style={{ marginTop: 2 }}>
          <span
            title="返却送迎中（ピックアップ/ドロップオフ以外へ移動すると解除）"
            style={{
              display: 'inline-block',
              fontSize: '0.5rem', fontWeight: 700, padding: '0 3px', borderRadius: '2px',
              backgroundColor: '#6d28d9', color: '#fff', border: '1px solid #5b21b6',
              lineHeight: 1.5, whiteSpace: 'nowrap',
            }}
          >返却送迎</span>
          {shuttleRes.map(sr => (
            <Typography
              key={sr.reservationId}
              sx={{ fontSize: '0.65rem', lineHeight: 1.2, fontWeight: 700, color: '#5b21b6' }}
              noWrap
            >
              {sr.time} {truncCustomerName(sr.customerName)}
            </Typography>
          ))}
        </div>
      )}
      {primary && (() => {
        // 両方の予約がある場合、貸出が当日なら赤表示
        const isDepartureToday = primary && secondary && primaryLabel === '貸出' && resInfo?.departureRes?.isToday;
        // 未来の予約（明日以降）はグレー表示
        const isFuture = !primary.isToday && /^[\d]+\//.test(primary.time);
        const futureColor = isFuture ? '#6b7280' : undefined;
        return (
          <>
            <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.9, color: isDepartureToday ? '#d32f2f' : futureColor || 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }}>
              {primaryLabel} {primary.time}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.8, color: isDepartureToday ? '#d32f2f' : futureColor || 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }} noWrap>
              {truncCustomerName(primary.customerName)}
            </Typography>
          </>
        );
      })()}
      {secondary && (() => {
        const isDepartureToday = primary && secondary && secondaryLabel === '貸出' && resInfo?.departureRes?.isToday;
        return (
          <>
            <Typography sx={{ fontSize: '0.6rem', lineHeight: 1.2, opacity: isDepartureToday ? 0.9 : 0.6, color: isDepartureToday ? '#d32f2f' : 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }}>
              {secondaryLabel} {secondary.time}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', lineHeight: 1.2, opacity: isDepartureToday ? 0.8 : 0.5, color: isDepartureToday ? '#d32f2f' : 'inherit', fontWeight: isDepartureToday ? 700 : 'inherit' }} noWrap>
              {truncCustomerName(secondary.customerName)}
            </Typography>
          </>
        );
      })()}
      {!primary && !secondary && shuttleRes.length === 0 && (
        <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.5 }}>-</Typography>
      )}
    </Paper>
  );
}

// --- DraggableVehicleCard ---
function DraggableVehicleCard({ vehicle, resInfo, onClick, onLocationClear, onToggleLocation, locStatusMap, lineUserMap, onOpenLineChat, highlight, preparing }: {
  vehicle: any;
  resInfo?: VehicleResInfo | null;
  onClick?: () => void;
  onLocationClear?: (vehicle: any) => void;
  /** P.U / D.O の車両だけ: 移動ステータスの表示を 行き ⇄ 戻り の切替ボタンにする */
  onToggleLocation?: () => void;
  locStatusMap?: Record<number, { label: string; bg_color?: string; text_color?: string }>;
  lineUserMap?: Record<string, any>;
  onOpenLineChat?: (lineUser: any, reservationId: string) => void;
  /** 貸出返却リストの「⬇」から指定された対象カード（一定時間グローさせる） */
  highlight?: boolean;
  /** 送迎車/返却車でお客様を運ぶ間の待機中（黄色リングで点滅。出発中になるまで） */
  preparing?: boolean;
}) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `vehicle-${vehicle.id}`,
    data: { vehicle },
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `vehicle-drop-${vehicle.id}`,
    data: { vehicle },
  });
  const locStatus = vehicle.location_status;
  // 移動ステータスが入っている＝どこかへ向かっている、で点滅させる（2026-09-01）。
  // 以前は id 1〜3 決め打ちだったため、那覇の 駅_南←(4) / 駅_北←(5) / 店→ヤ(6) /
  // 回送中(7) では点滅せず、宮古のように採番が違う店舗でも当たらなかった。
  // マスタの値はすべて「移動中」を表すので、null かどうかだけで判定する
  const isMoving = locStatus != null;

  return (
    <div
      ref={(node) => { setDragRef(node); setDropRef(node); }}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        // 判定（P.U/D.O は 行き⇄戻り の切替、ほかは移動ステータスのクリア）は呼び出し側
        if (onLocationClear) {
          onLocationClear(vehicle);
        }
      }}
      // 「⬇」ジャンプ時にこの id を目印にスクロールする
      data-vehicle-card-id={String(vehicle.id)}
      style={{
        opacity: isDragging ? 0.3 : 1,
        // 移動中に加え、お客様を運ぶ車の表示（送迎車 / 別1234 / 返8118）があるときも点滅する
        animation: (isMoving || resInfo?.transportTag) ? 'vehiclePulse 2s ease-in-out infinite' : 'none',
        outline: isOver && !isDragging ? '2px dashed #3b82f6' : 'none',
        outlineOffset: '2px',
        borderRadius: isOver && !isDragging ? '8px' : undefined,
        transition: 'outline 0.15s',
        position: 'relative',
      }}
    >
      <VehicleCard vehicle={vehicle} resInfo={resInfo} locStatusMap={locStatusMap} lineUserMap={lineUserMap} onOpenLineChat={onOpenLineChat} onToggleLocation={onToggleLocation} />
      {preparing && !highlight && (
        <span
          data-testid="vehicle-preparing"
          style={{
            position: 'absolute', inset: 0, borderRadius: 4, pointerEvents: 'none',
            boxShadow: 'inset 0 0 0 3px #fbc02d',
            animation: 'vehiclePrepareGlow 1.6s ease-in-out infinite',
          }}
        />
      )}
      {highlight && (
        <span
          style={{
            position: 'absolute', inset: 0, borderRadius: 4, pointerEvents: 'none',
            boxShadow: 'inset 0 0 0 3px #2563eb',
            animation: 'vehicleLocateGlow 1s ease-in-out infinite',
          }}
        />
      )}
    </div>
  );
}

// --- DraggableReservationCard（予約カード、DnD可能） ---
function DraggableReservationCard({ reservation, onClick, children }: {
  reservation: any;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `reservation-${reservation.id}`,
    data: { reservation },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{ opacity: isDragging ? 0.3 : 1 }}
    >
      <Paper elevation={0} sx={{
        px: 0.6, py: 0.3, borderRadius: 1, cursor: 'grab',
        backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0',
        opacity: 0.7, '&:hover': { boxShadow: 2, opacity: 1 },
      }}>
        {children}
      </Paper>
    </div>
  );
}

// --- PickupSubZone（ピックアップ内サブエリア、ドロップ可能） ---
function PickupSubZone({ id, label, reservations, onReservationClick, borderColor }: {
  id: string;
  label: string;
  reservations: any[];
  onReservationClick: (r: any) => void;
  borderColor: string;
}) {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <Box ref={setNodeRef} sx={{
      border: `1px solid ${isOver ? UI.color.accent : UI.color.border}`,
      borderRadius: 1,
      backgroundColor: isOver ? UI.color.accentBg : UI.color.bgCard,
      p: 0.5, flex: '1 1 0', minHeight: 32,
      display: 'flex', flexDirection: 'column',
      transition: 'background-color 0.15s',
      overflow: 'hidden',
    }}>
      <Typography sx={{ fontSize: UI.font.xs, fontWeight: 700, color: UI.color.textSub, mb: 0.2, lineHeight: 1, flexShrink: 0 }}>
        {label} {reservations.length > 0 && `(${reservations.length})`}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, flex: 1, overflowY: 'auto' }}>
        {reservations.map((r: any) => {
          const truncName = (r.customer_name || '').length > 6
            ? (r.customer_name || '').slice(0, 6) + '...' : (r.customer_name || '');
          return (
            <DraggableReservationCard key={r.id} reservation={r} onClick={() => onReservationClick(r)}>
              <Typography sx={{ fontSize: UI.font.xs, color: UI.color.textSub, lineHeight: 1.1 }}>
                {dayjs(r.start_date).format('HH:mm')}
              </Typography>
              <Typography sx={{ fontSize: UI.font.xs, fontWeight: 600, lineHeight: 1.1 }}>
                {truncName}
              </Typography>
            </DraggableReservationCard>
          );
        })}
      </Box>
    </Box>
  );
}

// --- 予約リスト内「連絡済み」サブエリア（2026-08-10 追加） ---
// こちらから電話連絡した予約をここに置く。空のときは使い方をうっすら表示する。
function CalledSubZone({ children, count }: { children: React.ReactNode; count: number }) {
  const { isOver, setNodeRef } = useDroppable({ id: CALLED_DROP_ID });
  return (
    <Box ref={setNodeRef} sx={{
      border: `1px dashed ${isOver ? UI.color.accent : UI.color.borderStrong}`,
      backgroundColor: isOver ? UI.color.accentBg : 'rgba(255,255,255,0.5)',
      borderRadius: 1,
      p: 0.4,
      mb: 0.5,
      flexShrink: 0,
      transition: 'background-color 0.15s, border-color 0.15s',
    }}>
      <Typography sx={{ fontSize: UI.font.xs, fontWeight: 700, color: UI.color.textSub, lineHeight: 1.4 }}>
        連絡済み（{count}）
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, minHeight: 22 }}>
        {children}
        {count === 0 && (
          <Typography sx={{ fontSize: '0.55rem', color: UI.color.textMuted, opacity: 0.7, lineHeight: 1.3 }}>
            電話連絡をした場合こちらに設置
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// --- ReservationListZone（予約リスト、ドロップ可能） ---
function ReservationListZone({ children, count, calledArea }: { children: React.ReactNode; count: number; calledArea?: React.ReactNode }) {
  const { isOver, setNodeRef } = useDroppable({ id: 'reservation_list' });
  return (
    <Box ref={setNodeRef} sx={{
      gridArea: 'r',
      border: `1px solid ${isOver ? UI.color.accent : UI.color.border}`,
      borderTop: `3px solid ${isOver ? UI.color.accent : R_ZONE.borderColor}`,
      borderRadius: 1,
      backgroundColor: isOver ? UI.color.accentBg : R_ZONE.color,
      p: 0.8, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'background-color 0.15s',
    }}>
      <ZoneHeaderDrop zoneId="reservation_list" color={R_ZONE.borderColor}>
      <Typography sx={{
        fontSize: UI.font.sm, fontWeight: 700, color: R_ZONE.borderColor,
        lineHeight: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
      }}>
        予約リスト
        {count > 0 && (
          <Box component="span" sx={{
            backgroundColor: R_ZONE.borderColor, color: '#fff', borderRadius: '50%',
            width: 16, height: 16, display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', fontSize: UI.font.xs, fontWeight: 700,
          }}>
            {count}
          </Box>
        )}
      </Typography>
      </ZoneHeaderDrop>
      {/* 「連絡済み」サブエリアはリストの上部に置く */}
      {calledArea}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, flex: 1, overflowY: 'auto' }}>
        {children}
      </Box>
    </Box>
  );
}

// --- サブグループ ---
interface SubGroup {
  label: string;
  vehicles: any[];
  /** 指定するとこのサブグループ自体がドロップ先になる（例: 店舗の「直接来店」。2026-08-10） */
  dropId?: string;
  /** true で次のサブグループと横並びに出す（出発準備｜出発案内。2026-10-21） */
  sideBySideWithNext?: boolean;
}

/** ドロップ可能なサブグループの枠。ゾーン内の一区画を独立した受け皿にする */
function DroppableSubGroup({ id, children }: { id: string; children: React.ReactNode }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        border: `1px dashed ${isOver ? UI.color.accent : UI.color.borderStrong}`,
        backgroundColor: isOver ? UI.color.accentBg : 'transparent',
        borderRadius: 1,
        p: 0.4,
        height: '100%',
        boxSizing: 'border-box',
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      {children}
    </Box>
  );
}

// --- ゾーン見出し = ゾーン本体のドロップ先（2026-10-21） ---
// サブエリア（出発準備・連絡済み等）や車両カードでゾーンが埋まると、本体の空き地に落とすのが難しい。
// 見出しの行をそのまま本体の受け皿にし、ドラッグ中だけ「ここに置く」を出す（高さは増やさない）
function ZoneHeaderDrop({ zoneId, disabled, color, children }: {
  zoneId: string; disabled?: boolean; color: string; children: React.ReactNode;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: `${zoneId}${MAIN_DROP_SUFFIX}`, disabled });
  const { active } = useDndContext();
  const dragging = !!active && !disabled;
  return (
    <Box
      ref={setNodeRef}
      data-testid={`zone-main-drop-${zoneId}`}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3, minHeight: 22,
        mx: -0.4, px: 0.4, borderRadius: 1, flexShrink: 0,
        border: `1px dashed ${isOver ? UI.color.accent : (dragging ? color : 'transparent')}`,
        backgroundColor: isOver ? UI.color.accentBg : (dragging ? 'rgba(255,255,255,0.65)' : 'transparent'),
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      {children}
      {dragging && (
        <Box component="span" sx={{ ml: 'auto', fontSize: UI.font.xs, fontWeight: 700, whiteSpace: 'nowrap', color: isOver ? UI.color.accent : color }}>
          ここに置く
        </Box>
      )}
    </Box>
  );
}

// --- DropZone ---
// --- Arrow（矢印、ゾーン辺に配置） ---
const ARROW_STYLE: React.CSSProperties = {
  position: 'absolute',
  fontSize: '1rem',
  fontWeight: 900,
  color: '#94a3b8',
  zIndex: 1,
  pointerEvents: 'none',
  lineHeight: 1,
};

function DropZone({ zone, children, count, subGroups, renderCard, collapsibleLast, flexChildren, arrows, expandTick, fillBody }: {
  zone: ZoneConfig;
  children?: React.ReactNode;
  count: number;
  subGroups?: SubGroup[];
  renderCard?: (vehicle: any) => React.ReactNode;
  collapsibleLast?: boolean;
  flexChildren?: boolean;
  arrows?: { position: 'top' | 'bottom' | 'left' | 'right'; label: string }[];
  /** 値が変わったら折りたたみ中のグループを開く（「⬇」ジャンプ用。2026-08-10） */
  expandTick?: number;
  /** true でスクロール本体を absolute 敷きにする（中身の量でゾーンが縦に伸びるのを防ぐ） */
  fillBody?: boolean;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: zone.id, disabled: zone.droppable === false });
  const [lastGroupOpen, setLastGroupOpen] = useState(false);
  useEffect(() => { if (expandTick) setLastGroupOpen(true); }, [expandTick]);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        gridArea: zone.gridArea,
        border: `1px solid ${isOver ? UI.color.accent : UI.color.border}`,
        borderTop: `3px solid ${isOver ? UI.color.accent : zone.borderColor}`,
        borderRadius: 1,
        backgroundColor: isOver ? UI.color.accentBg : zone.color,
        p: 0.8,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        position: 'relative',
        transition: 'background-color 0.15s, border-color 0.15s',
        overflow: 'visible',
        minHeight: 0,
      }}
    >
      {/* 矢印 */}
      {arrows?.map((a, i) => (
        <span key={i} style={{
          ...ARROW_STYLE,
          ...(a.position === 'top' ? { top: -2, left: '50%', transform: 'translate(-50%, -100%)' } :
            a.position === 'bottom' ? { bottom: -2, left: '50%', transform: 'translate(-50%, 100%)' } :
              a.position === 'left' ? { left: -2, top: '50%', transform: 'translate(-100%, -50%)' } :
                { right: -2, top: '50%', transform: 'translate(100%, -50%)' }),
        }}>
          {a.label}
        </span>
      ))}
      <ZoneHeaderDrop zoneId={zone.id} disabled={zone.droppable === false} color={zone.borderColor}>
      <Typography sx={{
        fontSize: UI.font.sm,
        fontWeight: 700,
        color: zone.borderColor,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        lineHeight: 1.2,
      }}>
        <span style={{ whiteSpace: 'pre-line' }}>{zone.label}</span>
        {count > 0 && (
          <Box component="span" sx={{
            backgroundColor: zone.borderColor,
            color: '#fff',
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: UI.font.xs,
            fontWeight: 700,
          }}>
            {count}
          </Box>
        )}
      </Typography>
      </ZoneHeaderDrop>
      {subGroups ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {subGroups.map((sg, idx) => {
            // 前のサブグループと横並びにした分は、そちらで描画済み（出発準備｜出発案内）
            if (idx > 0 && subGroups[idx - 1].sideBySideWithNext) return null;
            const isLast = idx === subGroups.length - 1;
            const isCollapsible = collapsibleLast && isLast && sg.vehicles.length > 0;
            const bodyOf = (g: SubGroup) => (
              <>
                <Typography
                  sx={{ fontSize: UI.font.xs, color: UI.color.textSub, mb: 0.3, fontWeight: 600, userSelect: 'none' }}
                >
                  {g.label}（{g.vehicles.length}）
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))',
                  gap: 0.5,
                  alignContent: 'start',
                  minHeight: 24, // 空でもドロップできる高さを確保
                }}>
                  {g.vehicles.map((v: any) => renderCard ? renderCard(v) : null)}
                </Box>
              </>
            );
            const body = bodyOf(sg);
            // ドロップ可能サブグループ（例: 店舗の「出発準備」「出発案内」）
            if (sg.dropId) {
              const next = sg.sideBySideWithNext ? subGroups[idx + 1] : undefined;
              return (
                <Box key={sg.label}>
                  {idx > 0 && <Box sx={{ borderTop: `1px dashed ${UI.color.borderStrong}`, my: 0.5 }} />}
                  {next ? (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'stretch' }}>
                      <Box sx={{ flex: '1 1 0', minWidth: 0 }}><DroppableSubGroup id={sg.dropId}>{body}</DroppableSubGroup></Box>
                      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        {next.dropId ? <DroppableSubGroup id={next.dropId}>{bodyOf(next)}</DroppableSubGroup> : bodyOf(next)}
                      </Box>
                    </Box>
                  ) : (
                    <DroppableSubGroup id={sg.dropId}>{body}</DroppableSubGroup>
                  )}
                </Box>
              );
            }
            return (
              <Box key={sg.label}>
                {idx > 0 && <Box sx={{ borderTop: `1px dashed ${UI.color.borderStrong}`, my: 0.5 }} />}
                <Typography
                  onClick={isCollapsible ? () => setLastGroupOpen(prev => !prev) : undefined}
                  sx={{
                    fontSize: UI.font.xs, color: UI.color.textSub, mb: 0.3, fontWeight: 600,
                    cursor: isCollapsible ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  {sg.label}（{sg.vehicles.length}）{isCollapsible ? (lastGroupOpen ? ' ▲' : ' ▼') : ''}
                </Typography>
                {(!isCollapsible || lastGroupOpen) && (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))',
                    gap: 0.5,
                    alignContent: 'start',
                  }}>
                    {sg.vehicles.map((v: any) => renderCard ? renderCard(v) : null)}
                  </Box>
                )}
                {sg.vehicles.length === 0 && !isCollapsible && (
                  <Typography sx={{ fontSize: UI.font.xs, color: UI.color.textMuted }}>-</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : fillBody ? (
        /* 2026-08-10: 台数が多いゾーン（利用中）専用。
           スクロール本体を absolute で敷くことで**中身の量がゾーンの高さに影響しない**ようにする。
           これにより高さは他ゾーン（グリッド行）に追従し、固定 maxHeight のような下部の余白も出ない。 */
        <Box sx={{ position: 'relative', flex: 1, minHeight: 80 }}>
          <Box sx={{
            position: 'absolute', inset: 0, overflowY: 'auto',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))', gap: 0.5, alignContent: 'start',
          }}>
            {children}
          </Box>
        </Box>
      ) : (
        <Box sx={{
          ...(flexChildren ? {
            display: 'flex', flexDirection: 'column', gap: 0.5,
          } : {
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))', gap: 0.5, alignContent: 'start',
          }),
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}>
          {/* 2026-08-10: 「残りN台を表示」の折りたたみを廃止し全件描画。
              ゾーン本体は overflowY:auto なので、はみ出す分はスクロールで確認できる
              （従来は slice で切り捨てており、ボタンを押すまで到達できなかった） */}
          {children}
        </Box>
      )}
    </Box>
  );
}

// --- メインコンポーネント ---
export default function VehiclePositionMap({
  vehicles,
  vehicleStatusList,
  departures,
  returns,
  upcomingStatuses,
  onVehicleClick,
  onStaffClick,
  onReservationClick,
  onOpenLineChat,
  lineUsers,
  staffList,
  locationStatusList,
  userId,
  locateVehicleId,
  locateNonce,
}: VehiclePositionMapProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // 「⬇」ジャンプ（2026-08-10）: 対象カードを一定時間グローさせ、畳まれたエリアは開く
  const [highlightVehicleId, setHighlightVehicleId] = useState<string | null>(null);
  const [expandTick, setExpandTick] = useState(0);
  useEffect(() => {
    if (!locateVehicleId) return;
    const vid = String(locateVehicleId);
    setExpandTick(t => t + 1);      // 折りたたみ中のグループを開く
    setHighlightVehicleId(vid);
    // 展開後にレイアウトが確定してからカードまでスクロールする。
    // ページ全体は親（daylist）が sticky 分を考慮して既に動かしているので、
    // ここでは**ゾーン内のスクロールコンテナだけ**を動かす（scrollIntoView だと
    // ページ位置まで動いて、せっかく合わせた表示位置がずれてしまう）
    const scrollTimer = setTimeout(() => {
      const el = document.querySelector(`[data-vehicle-card-id="${vid}"]`) as HTMLElement | null;
      if (!el) return;
      // ⚠️ ページ全体のスクローラ（App.tsx の <main>）まで遡ってはいけない。
      //    そこまで動かすとカードが画面最上部に来て、親が sticky 分を考慮して
      //    合わせたページ位置を上書きし、ヘッダーに潜り込んでしまう（2026-08-10 修正）。
      //    sticky ヘッダーを含む要素＝ページのスクローラとみなして打ち切る。
      const pageSticky = document.querySelector('[data-daylist-sticky]');
      let box: HTMLElement | null = el.parentElement;
      let target: HTMLElement | null = null;
      while (box) {
        if (pageSticky && box.contains(pageSticky)) break; // ページ側に到達したら中止
        const oy = window.getComputedStyle(box).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && box.scrollHeight > box.clientHeight) { target = box; break; }
        box = box.parentElement;
      }
      if (!target) return; // ゾーン内にスクロール余地が無ければ何もしない（ページは動かさない）
      const delta = el.getBoundingClientRect().top - target.getBoundingClientRect().top;
      target.scrollTo({ top: target.scrollTop + delta - 8, behavior: 'smooth' });
    }, 400);
    const clearTimer = setTimeout(() => setHighlightVehicleId(null), 4000);
    return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer); };
  }, [locateVehicleId, locateNonce]);
  const [activeVehicle, setActiveVehicle] = useState<any>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });
  const [vehicleSwapConfirm, setVehicleSwapConfirm] = useState<{
    sourceVehicle: any; targetVehicle: any;
    sourceRes: any; targetRes: any;
  } | null>(null);
  // ゾーン → 移動ステータス id（code から実行時解決。宮古のようにマスタ未整備なら null）
  const zoneLocationId = useCallback((zoneId: string): number | null => {
    const code = ZONE_LOCATION_CODES[zoneId];
    if (!code) return null;
    const hit = (locationStatusList || []).find((s: any) => s?.code === code);
    return typeof hit?.id === 'number' ? hit.id : null;
  }, [locationStatusList]);

  // 位置ステータスの色マップ
  const locStatusMap = useMemo(() => {
    const map: Record<number, { label: string; bg_color?: string; text_color?: string }> = {};
    (locationStatusList || []).forEach((s: any) => {
      if (s.id) map[s.id] = { label: s.label || '', bg_color: s.bg_color, text_color: s.text_color };
    });
    return map;
  }, [locationStatusList]);

  // 動的ステータスマップ: vehicleStatusList から status_code ⇄ status_id の双方向マップを生成。
  // ZONES は statusCode で宣言されているため、APIで取得した最新の id 採番に追従できる。
  const statusIdByCode = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    (vehicleStatusList || []).forEach((s: any) => {
      if (s?.code && typeof s.id === 'number') map[s.code] = s.id;
    });
    return map;
  }, [vehicleStatusList]);

  // status_id → zone_id マッピング（動的）。
  // 車両の振り分け先は droppable な実体ゾーンに限定し、droppable: false のラベル専用ゾーン（airport_1f等）は除外する。
  // 同一 statusCode に複数の droppable ゾーンが存在する場合は PREFERRED_ZONE_BY_CODE で明示する。
  const statusToZone = useMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {};
    ZONES.forEach(z => {
      if (!z.statusCode) return;
      if (z.droppable === false) return;
      const id = statusIdByCode[z.statusCode];
      if (typeof id !== 'number') return;
      const preferred = PREFERRED_ZONE_BY_CODE[z.statusCode];
      if (preferred && z.id !== preferred) return;
      map[id] = z.id;
    });
    return map;
  }, [statusIdByCode]);

  // LINE ユーザーマップ: reservation_id → lineUser（ReservationTableと同じロジック）
  const lineUserMap = useMemo(() => {
    const map: Record<string, any> = {};
    if (!Array.isArray(lineUsers)) return map;
    lineUsers.forEach((u: any) => {
      const rids = u.reservation_ids || [];
      rids.forEach((rid: any) => {
        if (!String(rid).startsWith('STAFF:')) {
          map[String(rid)] = u;
        }
      });
    });
    return map;
  }, [lineUsers]);

  // 楽観的更新用: vehicle_id → 一時的な statusId（API完了前に即座にゾーン移動）
  const [optimisticStatus, setOptimisticStatus] = useState<Record<string, number>>({});
  // 楽観的に貸出エリアに移動した車両ID
  const [optimisticDeparted, setOptimisticDeparted] = useState<Set<string>>(new Set());
  // 予約の楽観的更新用: reservationId → { 到着連絡, 空港 } (null=削除)
  // called: 予約リストの「連絡済み」サブエリア用（こちらから電話連絡した印。2026-08-10 追加）
  // altNumber: 別車両番号（2026-11-04）
  const [optimisticResJson, setOptimisticResJson] = useState<Record<string, { arrival: string | null; airport: string | null; called?: string | null; altNumber?: string }>>({});
  // 返却送迎（送迎車モード）の楽観的更新: vehicle_id → 予約IDリスト（[] = 解除。2026-08-24）
  const [optimisticShuttle, setOptimisticShuttle] = useState<Record<string, string[]>>({});
  // 出発案内の楽観的更新: vehicle_id → true（入れた）/ false（外した）（2026-10-21）
  const [optimisticGuide, setOptimisticGuide] = useState<Record<string, boolean>>({});
  const isGuideVehicle = useCallback((v: any): boolean => {
    const opt = optimisticGuide[String(v.id)];
    return opt !== undefined ? opt : hasGuideToday(v);
  }, [optimisticGuide]);
  // 返却送迎の送り先予約を選ぶモーダル
  // targetZoneId: 落とした先のピックアップ。車両はモーダルの回答後に動かす（キャンセルで元の場所のまま。2026-11-04）
  const [shuttleAssign, setShuttleAssign] = useState<{ vehicle: any; targetZoneId: string } | null>(null);
  // 「別車両」の車両番号を聞くモーダル（2026-11-04）
  const [altShuttleAsk, setAltShuttleAsk] = useState<{ vehicle: any; reservation: any; number: string } | null>(null);
  // 店舗「出発準備」へ車両をドロップしたとき、お客様の受け方（送迎車/返却車/直接来店）を選ぶモーダル（2026-09-19）
  const [prepareAssign, setPrepareAssign] = useState<{ vehicle: any; reservation: any } | null>(null);
  const [shuttleSelected, setShuttleSelected] = useState<string[]>([]);
  // ドラッグ中の予約カード（DragOverlay用）
  const [activeReservation, setActiveReservation] = useState<any>(null);
  // 予約モーダル
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [isSavingArrival, setIsSavingArrival] = useState(false);
  // 貸出済み確認モーダル（利用中→空港店移動時 / ドロップオフ→非ドロップオフ移動時）
  // noToastOnDecline=true の場合、「いいえ」押下時に追加 toast を出さない（既に移動成功 toast 済みのため）
  const [doneConfirm, setDoneConfirm] = useState<{ vehicle: any; returnReservation: any; noToastOnDecline?: boolean } | null>(null);
  // 車両カードクリックモーダル
  const [selectedVehicleForModal, setSelectedVehicleForModal] = useState<any>(null);

  // Context から全予約を取得（明日の貸出判定用）
  const { reservations: contextReservations } = useReservationData() as any;

  // 明日貸出の車両IDセット（全予約データから直接計算）
  const tomorrowDepIds = useMemo(() => {
    const tomorrow = dayjs().add(1, 'day');
    const ids = new Set<string>();
    if (!Array.isArray(contextReservations)) return ids;
    contextReservations.forEach((r: any) => {
      if (!r.vehicle_id) return;
      if (EXCLUDED_RES_STATUSES.has(String(r.status))) return;
      const s = dayjs(r.start_date);
      if (s.isValid() && s.format('YYYY-MM-DD') === tomorrow.format('YYYY-MM-DD')) {
        ids.add(String(r.vehicle_id));
      }
    });
    return ids;
  }, [contextReservations]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const isExcludedRes = (r: any) => EXCLUDED_RES_STATUSES.has(String(r.status));

  // 除外後の貸出・返却
  const filteredDepartures = useMemo(() => departures.filter((r: any) => !isExcludedRes(r)), [departures]);
  const filteredReturns = useMemo(() => returns.filter((r: any) => !isExcludedRes(r)), [returns]);

  // 返却送迎中の車両: vehicle_id → 送迎する予約IDリスト（楽観的更新を優先。2026-08-24）
  const shuttleIdsByVehicle = useMemo(() => {
    const map: Record<string, string[]> = {};
    Object.values(vehicles).forEach((v: any) => {
      const vid = String(v.id);
      const ids = optimisticShuttle[vid] !== undefined ? optimisticShuttle[vid] : getShuttleResIds(v);
      if (ids.length) map[vid] = ids;
    });
    return map;
  }, [vehicles, optimisticShuttle]);

  // 予約マップ: 各車両の「返却予約」と「貸出予約」を分けて管理
  const reservationInfoMap = useMemo(() => {
    const map: Record<string, VehicleResInfo> = {};
    const today = dayjs().format('YYYY-MM-DD');

    // スタッフ解決ヘルパー（ReservationTableと同じロジック）
    const resolveDriver = (staffRaw: any, r: any, type: 'departure' | 'return') => {
      if (!staffRaw) return null;
      let staff = (Array.isArray(staffList) ? staffList.find(s => s.id === staffRaw.id) : null) || staffRaw;
      // スポットスタッフ（タイミー）の場合: メモからアサイン名を取得。
      // ID 決め打ちは店舗ごとの採番差で別人に当たるため名前で判定する
      if (staff && isSpotStaff(staff)) {
        const memo = r.detail?.memo || r.memo || '';
        const assignLabel = type === 'departure' ? '貸出運転者アサイン' : '返却運転者アサイン';
        const memoRegex = new RegExp(`---${assignLabel} [\\d-]+ [\\d:]+---\\n(.+?)\\n---`);
        const match = memo.match(memoRegex);
        if (match) {
          staff = { ...staff, staff_name: match[1], name: match[1] };
        }
      }
      return staff;
    };

    // 返却の運転者（do_staff）
    const returnDriverMap: Record<string, any> = {};
    filteredReturns.forEach((r: any) => {
      if (!r.vehicle_id) return;
      const vid = String(r.vehicle_id);
      if (!map[vid]) map[vid] = {};
      map[vid].returnRes = {
        time: dayjs(r.end_date).format('HH:mm'),
        customerName: r.customer_name || '',
        reservationId: String(r.id),
        isToday: dayjs(r.end_date).format('YYYY-MM-DD') === today,
      };
      // 返却予約のメタ（貸出予約があれば後で上書きされる）
      if (!map[vid].meta) map[vid].meta = buildResMeta(r, 'return');
      const driver = resolveDriver(r.do_staff, r, 'return');
      if (driver) returnDriverMap[vid] = driver;
    });

    // 貸出の運転者（pu_staff）
    const depDriverMap: Record<string, any> = {};
    // 返却送迎で運ぶ予約 → 運ぶ車両のコード（正は車両の json_data「返却送迎」。2026-11-04）
    const returnCarByResId: Record<string, string> = {};
    Object.entries(shuttleIdsByVehicle).forEach(([vid, ids]) => {
      ids.forEach(id => { returnCarByResId[id] = String(vehicles[vid]?.vehicle_code || ''); });
    });
    filteredDepartures.forEach((r: any) => {
      if (!r.vehicle_id) return;
      const vid = String(r.vehicle_id);
      if (!map[vid]) map[vid] = {};
      // お客様を運ぶ車の表示。出発中の車両・利用中の予約には出さない
      const opt = optimisticResJson[String(r.id)];
      const contact = opt !== undefined
        ? { on: opt.arrival === ARRIVAL_CONTACT_ON, airport: String(opt.airport ?? '') }
        : getArrivalContact(r);
      const vehicleStatusId = optimisticStatus[vid] ?? Number(vehicles[vid]?.status);
      const started = vehicleStatusId === statusIdByCode['departing']
        || String(r.status) === '3' || r.status === 'in_use';
      map[vid].transportTag = (!started && transportLabel({
        on: contact.on,
        airport: contact.airport,
        altNumber: opt?.altNumber !== undefined ? opt.altNumber : getAltShuttleNumber(r),
        returnCarCode: returnCarByResId[String(r.id)],
      })) || undefined;
      map[vid].departureRes = {
        time: dayjs(r.start_date).format('HH:mm'),
        customerName: r.customer_name || '',
        reservationId: String(r.id),
        isToday: dayjs(r.start_date).format('YYYY-MM-DD') === today,
        airport: getArrivalContact(r).airport,
      };
      // 貸出予約のメタを優先（受免支は貸出前チェックのため）
      map[vid].meta = buildResMeta(r, 'departure');
      const driver = resolveDriver(r.pu_staff, r, 'departure');
      if (driver) depDriverMap[vid] = driver;
    });

    // ドライバー情報をセット（ステータスに応じて適切な方を選択）
    Object.keys(map).forEach(vid => {
      const vehicle = Object.values(vehicles).find((v: any) => String(v.id) === vid) as any;
      const statusId = vehicle ? Number(vehicle.status) : 0;
      const staff = (statusId === 1 || statusId === 10)
        ? (returnDriverMap[vid] || depDriverMap[vid])
        : (depDriverMap[vid] || returnDriverMap[vid]);
      if (staff) {
        map[vid].driverName = staff.staff_name || staff.name || '';
        map[vid].driverBgColor = staff.bg_color || undefined;
        map[vid].driverTextColor = staff.text_color || undefined;
      }
    });

    // 未来の貸出予約を追加（ヤード「予定なし」等で次回予約を表示するため）
    if (Array.isArray(contextReservations)) {
      const now = dayjs();
      const tomorrow = now.add(1, 'day');
      // 車両ごとに最も近い未来の貸出予約を探す
      const futureDepByVehicle: Record<string, any> = {};
      contextReservations.forEach((r: any) => {
        if (!r.vehicle_id) return;
        if (EXCLUDED_RES_STATUSES.has(String(r.status))) return;
        const s = dayjs(r.start_date);
        if (!s.isValid() || s.isBefore(now, 'day')) return;
        if (s.isSame(now, 'day')) return; // 今日は既にfilteredDeparturesで処理済み
        const vid = String(r.vehicle_id);
        if (!futureDepByVehicle[vid] || s.isBefore(dayjs(futureDepByVehicle[vid].start_date))) {
          futureDepByVehicle[vid] = r;
        }
      });
      Object.entries(futureDepByVehicle).forEach(([vid, r]) => {
        if (!map[vid]) map[vid] = {};
        if (!map[vid].departureRes) {
          const s = dayjs(r.start_date);
          const label = s.isSame(tomorrow, 'day') ? '明日' : s.format('M/D');
          map[vid].departureRes = {
            time: `${label} ${s.format('HH:mm')}`,
            customerName: r.customer_name || '',
            reservationId: String(r.id),
          };
        }
      });
    }

    // 返却送迎（送迎車モード）: 迎えに行く予約を載せ、本来紐づく貸出予約は落とす。
    // 迎える相手はこの車両と無関係な予約なので、次の貸出予約を並べると誤解を招くため（2026-08-24）
    Object.entries(shuttleIdsByVehicle).forEach(([vid, ids]) => {
      const list = ids
        .map(id => {
          const r = filteredDepartures.find((x: any) => String(x.id) === id)
            || (Array.isArray(contextReservations) ? contextReservations.find((x: any) => String(x.id) === id) : null);
          if (!r) return null;
          return {
            time: dayjs(r.start_date).format('HH:mm'),
            customerName: r.customer_name || '',
            reservationId: String(r.id),
          };
        })
        .filter(Boolean) as { time: string; customerName: string; reservationId: string }[];
      if (!list.length) return;
      if (!map[vid]) map[vid] = {};
      map[vid].shuttleRes = list;
      delete map[vid].departureRes;
      delete map[vid].transportTag;
    });

    return map;
  }, [filteredDepartures, filteredReturns, contextReservations, staffList, vehicles, shuttleIdsByVehicle, optimisticResJson, optimisticStatus, statusIdByCode]);

  // departing の車両を「利用中(E)」と「貸出(H)」に振り分け
  // 返却予約(A)がまだ完了していない → 利用中（Aのために出発中）
  // 返却予約がない or 完了済み → 貸出（Bのために出発中）
  // ※ departing の id はステータスマスタから動的に解決（店舗別の採番差異に対応）
  const { departedTodayVehicles, inUseVehiclesFromDeparting } = useMemo(() => {
    const departureVehicleIds = new Set(filteredDepartures.map((r: any) => String(r.vehicle_id)).filter(Boolean));
    const returnVehicleIds = new Set(filteredReturns.map((r: any) => String(r.vehicle_id)).filter(Boolean));

    const departed: any[] = [];
    const inUse: any[] = [];
    const departingStatusId = statusIdByCode['departing'];

    Object.values(vehicles).forEach((v: any) => {
      if (v.out_of_use) return;
      if (typeof departingStatusId !== 'number' || Number(v.status) !== departingStatusId) return;
      const vid = String(v.id);

      if (returnVehicleIds.has(vid)) {
        // 返却予約がまだある → 利用中（返却予約のために出発中）
        inUse.push(v);
      } else if (departureVehicleIds.has(vid)) {
        // 返却予約なし + 貸出予約あり → 貸出（貸出予約のために出発中）
        departed.push(v);
      }
    });

    departed.sort((a: any, b: any) => (a.vehicle_code || '').localeCompare(b.vehicle_code || ''));
    inUse.sort((a: any, b: any) => (a.vehicle_code || '').localeCompare(b.vehicle_code || ''));
    return { departedTodayVehicles: departed, inUseVehiclesFromDeparting: inUse };
  }, [vehicles, filteredDepartures, filteredReturns, statusIdByCode]);

  // 車両をゾーンにグループ分け
  const vehiclesByZone = useMemo(() => {
    const groups: Record<string, any[]> = {};
    ZONES.forEach(z => { groups[z.id] = []; });

    const vehicleList = Object.values(vehicles);
    const todayVehicleIds = new Set([
      ...filteredDepartures.map((r: any) => String(r.vehicle_id)),
      ...filteredReturns.map((r: any) => String(r.vehicle_id)),
    ].filter(Boolean));

    // H（貸出）とE特別枠（利用中からのdeparting）に表示する車両IDセット
    const departedTodayIds = new Set(departedTodayVehicles.map((v: any) => String(v.id)));
    const inUseDepartingIds = new Set(inUseVehiclesFromDeparting.map((v: any) => String(v.id)));

    vehicleList.forEach((v: any) => {
      if (v.out_of_use) return;
      const vid = String(v.id);
      // 楽観的更新があればそちらを優先
      const statusId = optimisticStatus[vid] ?? Number(v.status);

      // 楽観的に貸出エリアに移動した車両はメインゾーンから除外
      if (optimisticDeparted.has(vid)) return;

      // H（貸出）またはE特別枠に該当する車両はメインゾーンから除外
      if ((departedTodayIds.has(vid) || inUseDepartingIds.has(vid)) && !optimisticStatus[vid]) return;

      const zoneId = statusToZone[statusId];
      if (!zoneId) return;

      if (todayVehicleIds.has(vid) || ALWAYS_SHOW_ZONE_IDS.includes(zoneId)) {
        if (groups[zoneId]) {
          groups[zoneId].push(v);
        }
      }
    });

    // 利用中ゾーンに departing かつ返却予約ありの車両を追加
    inUseVehiclesFromDeparting.forEach((v: any) => {
      if (!optimisticStatus[String(v.id)]) {
        groups.in_use = groups.in_use || [];
        groups.in_use.push(v);
      }
    });

    Object.values(groups).forEach(arr => arr.sort((a: any, b: any) =>
      (a.vehicle_code || '').localeCompare(b.vehicle_code || '')
    ));

    // 利用中ゾーンは返却予定時刻の昇順（返却の迫った車両が上に来る。2026-07-27）
    // 返却予約が無い車両は末尾（車両コード順）
    if (groups.in_use && groups.in_use.length > 1) {
      const retTimeByVid: Record<string, string> = {};
      filteredReturns.forEach((r: any) => {
        if (r.vehicle_id && r.end_date) retTimeByVid[String(r.vehicle_id)] = String(r.end_date);
      });
      groups.in_use.sort((a: any, b: any) => {
        const ta = retTimeByVid[String(a.id)] || '9999-12-31';
        const tb = retTimeByVid[String(b.id)] || '9999-12-31';
        return ta.localeCompare(tb) || (a.vehicle_code || '').localeCompare(b.vehicle_code || '');
      });
    }

    return groups;
  }, [vehicles, filteredDepartures, filteredReturns, departedTodayVehicles, inUseVehiclesFromDeparting, optimisticStatus, optimisticDeparted, statusToZone]);

  // 空港店・ヤードのサブグループ（出発準備/本日貸出/明日貸出/予定なし）
  // withWalkin=true（店舗ゾーン）のとき、本日貸出のうち予約の「連絡」が 送迎車/返却車/直接来店 の
  // 車両を先頭の「出発準備」枠に出す（2026-09-19。以前は「直接来店」だけの枠だった）
  const buildSubGroups = useCallback((zoneVehicles: any[], withWalkin = false): SubGroup[] => {
    const today = dayjs().startOf('day');
    const tomorrow = today.add(1, 'day');
    const todayDep: any[] = [];
    const walkinDep: any[] = [];
    const guideVehicles: any[] = [];
    const tomorrowDep: any[] = [];
    const noDep: any[] = [];

    // filteredDepartures から本日貸出の車両IDセット
    const todayDepIds = new Set(filteredDepartures
      .filter((r: any) => dayjs(r.start_date).isSame(today, 'day'))
      .map((r: any) => String(r.vehicle_id))
      .filter(Boolean)
    );
    // 本日貸出のうち出発準備（送迎車/返却車/直接来店）の車両ID（予約の json_data.空港 が正。楽観的更新を優先参照）
    const walkinVehicleIds = new Set(
      filteredDepartures
        .filter((r: any) => {
          if (!dayjs(r.start_date).isSame(today, 'day')) return false;
          const opt = optimisticResJson[String(r.id)];
          if (opt !== undefined) return opt.arrival === ARRIVAL_CONTACT_ON && PREPARE_AIRPORT_VALUES.has(String(opt.airport));
          return isPreparingAtShop(getArrivalContact(r));
        })
        .map((r: any) => String(r.vehicle_id))
        .filter(Boolean)
    );

    zoneVehicles.forEach((v: any) => {
      const vid = String(v.id);
      // 出発案内（ピックアップから店舗へ連れてきた車両。2026-10-21）は出発準備・本日貸出より優先
      if (withWalkin && isGuideVehicle(v)) {
        guideVehicles.push(v);
        return;
      }
      if (todayDepIds.has(vid)) {
        if (withWalkin && walkinVehicleIds.has(vid)) walkinDep.push(v);
        else todayDep.push(v);
        return;
      }
      // 明日貸出かどうかを全予約データから判定
      if (tomorrowDepIds.has(vid)) {
        tomorrowDep.push(v);
        return;
      }
      noDep.push(v);
    });

    // ソート: 位置ステータスあり優先 → 時間昇順
    const sortByLocationThenTime = (arr: any[]) => arr.sort((a: any, b: any) => {
      const aHasLoc = a.location_status ? 1 : 0;
      const bHasLoc = b.location_status ? 1 : 0;
      if (aHasLoc !== bHasLoc) return bHasLoc - aHasLoc; // 位置ステータスあり優先
      const infoA = reservationInfoMap[String(a.id)];
      const infoB = reservationInfoMap[String(b.id)];
      const timeA = infoA?.departureRes?.time || infoA?.returnRes?.time || 'zz';
      const timeB = infoB?.departureRes?.time || infoB?.returnRes?.time || 'zz';
      return timeA.localeCompare(timeB);
    });

    return [
      // 出発準備（ドロップ可能・先頭）: ここへ車両カードを落とすと受け方（送迎車/返却車/直接来店）を聞く
      ...(withWalkin
        ? [
          { label: '出発準備', vehicles: sortByLocationThenTime(walkinDep), dropId: PREPARE_DROP_ID, sideBySideWithNext: true },
          { label: '出発案内', vehicles: sortByLocationThenTime(guideVehicles), dropId: GUIDE_DROP_ID },
        ]
        : []),
      { label: '本日貸出', vehicles: sortByLocationThenTime(todayDep) },
      { label: '明日貸出', vehicles: sortByLocationThenTime(tomorrowDep) },
      { label: '予定なし', vehicles: noDep },
    ];
  }, [filteredDepartures, tomorrowDepIds, reservationInfoMap, optimisticResJson, isGuideVehicle]);

  const airportSubGroups = useMemo(() => buildSubGroups(vehiclesByZone.airport || [], true), [vehiclesByZone.airport, buildSubGroups]);
  const yardSubGroups = useMemo(() => buildSubGroups(vehiclesByZone.yard || []), [vehiclesByZone.yard, buildSubGroups]);

  // 本日のbooked予約を「予約リスト（未連絡/連絡済み）」と「ピックアップ（到着連絡済み）」に分ける
  // 楽観的更新（optimisticResJson）を優先参照
  const { waitingReservations, calledReservations, arrivedReservations } = useMemo(() => {
    const all = filteredDepartures
      .filter((r: any) => {
        const s = String(r.status).toLowerCase();
        return s === '1' || s === 'booked';
      })
      .sort((a: any, b: any) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix());

    const waiting: any[] = [];
    const called: any[] = [];
    const arrived: any[] = [];
    all.forEach((r: any) => {
      const rid = String(r.id);
      const opt = optimisticResJson[rid];
      const jd = (r.json_data || r.detail?.json_data)?.data || {};
      // 楽観的更新があればそちらを優先
      const hasArrival = opt !== undefined
        ? opt.arrival === '有り'
        : jd['到着連絡'] === '有り';
      if (hasArrival) {
        arrived.push(r);
        return;
      }
      // 到着連絡前でも、こちらから電話連絡済みなら「連絡済み」サブエリアへ
      const hasCalled = opt !== undefined && opt.called !== undefined
        ? opt.called === AIRPORT_CALLED
        : String(jd['空港'] ?? '').trim() === AIRPORT_CALLED;
      if (hasCalled) called.push(r);
      else waiting.push(r);
    });
    return { waitingReservations: waiting, calledReservations: called, arrivedReservations: arrived };
  }, [filteredDepartures, optimisticResJson]);

  // 予約カード（予約リスト / 連絡済みサブエリアで共用。2026-08-10）
  const renderReservationCard = useCallback((r: any) => {
    const truncName = (r.customer_name || '').length > 6
      ? (r.customer_name || '').slice(0, 6) + '...' : (r.customer_name || '');
    return (
      <DraggableReservationCard key={r.id} reservation={r} onClick={() => setSelectedReservation(r)}>
        <Typography sx={{ fontSize: '0.55rem', color: '#666', lineHeight: 1.2 }}>
          {dayjs(r.start_date).format('HH:mm')}
        </Typography>
        <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, lineHeight: 1.2 }}>
          {truncName}
        </Typography>
      </DraggableReservationCard>
    );
  }, []);

  // カードレンダー関数
  // 車両カードのダブルクリック（タップ）:
  //   ピックアップ / ドロップオフにいる車両 → 移動ステータスを 行き（→空港 等）⇄ 戻り（→店舗）で切り替える（2026-10-24）。
  //     エリアは業務（ピックアップ中・ドロップオフ中）を表し、移動ステータスはその中のサブステータス
  //   それ以外のエリアで移動ステータスが残っている車両 → クリア（「移動完了」。従来どおり）
  const handleLocationClear = useCallback(async (vehicle: any) => {
    const vid = String(vehicle.id);
    const zoneId = statusToZone[optimisticStatus[vid] ?? Number(vehicle.status)];
    try {
      if (zoneId && (PICKUP_ZONE_IDS.has(zoneId) || DROPOFF_ZONE_IDS.has(zoneId))) {
        const goingId = zoneLocationId(zoneId);
        const returnCode = ZONES_CONFIG.returnToShopLocationStatus;
        const returnHit = (locationStatusList || []).find((s: any) => s?.code === returnCode);
        const returnId = typeof returnHit?.id === 'number' ? returnHit.id : null;
        if (returnId == null) {
          setToast({ open: true, message: '移動ステータス「→店舗」が見つかりません', severity: 'error' });
          return;
        }
        // 行き（→空港 等）のときだけ 戻り（→店舗）へ。戻り・空・それ以外は 行き にする
        const toReturn = goingId != null && vehicle.location_status === goingId;
        await saveVehicleAndSync({ id: vehicle.id, location_status: toReturn ? returnId : goingId }, userId);
        setToast({ open: true, message: `${vehicle.vehicle_code} → ${toReturn ? '店舗へ戻り中' : '空港へ向かい中'}`, severity: 'success' });
        return;
      }
      if (vehicle.location_status == null) return;
      await saveVehicleAndSync({ id: vehicle.id, location_status: null }, userId);
      setToast({ open: true, message: `${vehicle.vehicle_code} 移動完了`, severity: 'success' });
    } catch {
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  }, [userId, statusToZone, optimisticStatus, zoneLocationId, locationStatusList]);

  // ピックアップ / ドロップオフにいる車両か（移動ステータスの 行き ⇄ 戻り ボタンを出す。2026-10-25）
  const canToggleLocation = useCallback((v: any): boolean => {
    const zoneId = statusToZone[optimisticStatus[String(v.id)] ?? Number(v.status)];
    return !!zoneId && (PICKUP_ZONE_IDS.has(zoneId) || DROPOFF_ZONE_IDS.has(zoneId));
  }, [statusToZone, optimisticStatus]);

  // 送迎車/返却車でお客様を運んでいる間、店舗で待つアサイン車両を点滅させる（出発中になるまで）。
  // 判定は予約の「連絡」（楽観的更新を優先）× 車両が出発中でないこと
  const isPreparingVehicle = useCallback((v: any): boolean => {
    const info = reservationInfoMap[String(v.id)];
    const dep = info?.departureRes;
    if (!dep?.isToday || !dep.reservationId) return false;
    const opt = optimisticResJson[dep.reservationId];
    const contact = opt !== undefined
      ? { on: opt.arrival === ARRIVAL_CONTACT_ON, airport: String(opt.airport ?? '') }
      : { on: !!dep.airport, airport: dep.airport || '' };
    if (!needsShuttleDriver(contact)) return false;
    const departingId = statusIdByCode['departing'];
    const statusId = optimisticStatus[String(v.id)] ?? Number(v.status);
    return departingId == null || statusId !== departingId;
  }, [reservationInfoMap, optimisticResJson, optimisticStatus, statusIdByCode]);

  const renderCard = useCallback((v: any) => (
    <DraggableVehicleCard
      key={v.id}
      vehicle={v}
      resInfo={reservationInfoMap[String(v.id)]}
      onClick={() => setSelectedVehicleForModal(v)}
      onLocationClear={handleLocationClear}
      onToggleLocation={canToggleLocation(v) ? () => handleLocationClear(v) : undefined}
      locStatusMap={locStatusMap}
      lineUserMap={lineUserMap}
      onOpenLineChat={onOpenLineChat}
      highlight={highlightVehicleId === String(v.id)}
      preparing={isPreparingVehicle(v)}
    />
  ), [reservationInfoMap, handleLocationClear, canToggleLocation, lineUserMap, onOpenLineChat, highlightVehicleId, isPreparingVehicle]);

  // 店舗「出発準備」: 紐づく本日の貸出予約を「到着連絡=有り / 空港=<受け方>」にする（2026-09-19）。
  //   受け方 = 送迎車 / 返却車 / 直接来店。予約リストのカードもピックアップの同名サブゾーンへ移る（表示は予約の値が正）。
  //   車両が店舗以外にいる場合は店舗ステータスへも移す。送迎車/返却車なら続けて運転者モーダルを出す。
  //   旧「直接来店」枠へのドロップ（2026-08-10）を一般化したもの。
  const applyPrepareContact = useCallback(async (vehicle: any, linkedDeparture: any, airportValue: string) => {
    const vid = String(vehicle.id);
    const rid = String(linkedDeparture.id);
    setOptimisticResJson(prev => ({ ...prev, [rid]: { arrival: ARRIVAL_CONTACT_ON, airport: airportValue } }));
    const shopStatusId = statusIdByCode['airport_store'];
    const moveToShop = typeof shopStatusId === 'number' && Number(vehicle.status) !== shopStatusId;
    if (moveToShop) setOptimisticStatus(prev => ({ ...prev, [vid]: shopStatusId }));
    try {
      const jd = linkedDeparture.json_data || linkedDeparture.detail?.json_data || { data: {} };
      const newJsonData = { ...jd, data: { ...(jd.data || {}), [ARRIVAL_CONTACT_KEY]: ARRIVAL_CONTACT_ON, [AIRPORT_KEY]: airportValue } };
      await saveReservationAndSync({
        ...linkedDeparture,
        json_data: newJsonData,
        detail: { ...(linkedDeparture.detail || {}), json_data: newJsonData },
      });
      if (moveToShop) {
        await saveVehicleAndSync({ id: vehicle.id, status: shopStatusId, location_status: zoneLocationId('airport') }, userId);
      }
      setToast({ open: true, message: `${vehicle.vehicle_code} → 出発準備（${airportValue} / ${linkedDeparture.customer_name || ''}）`, severity: 'success' });
      if (SHUTTLE_AIRPORT_VALUES.has(airportValue) && onStaffClick) {
        setTimeout(() => onStaffClick(linkedDeparture, 'driver', 'departure'), 300);
      }
    } catch (error) {
      console.error('Prepare contact update failed:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
    }
    setTimeout(() => {
      setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
    }, 5000);
  }, [statusIdByCode, zoneLocationId, userId, onStaffClick]);

  // 車両カードのポップアップ「送迎車」「別車両」（2026-11-04）。予約カードは空港1Fの送迎車サブエリアへ移り、
  // 車両はその場に残る。number 付き = 別車両。on=false で取り消し（予約カードは予約リストへ戻る）
  const applyShuttleContact = useCallback(async (reservation: any, on: boolean, number?: string) => {
    const rid = String(reservation.id);
    setOptimisticResJson(prev => ({
      ...prev,
      [rid]: on
        ? { arrival: ARRIVAL_CONTACT_ON, airport: AIRPORT_SHUTTLE, altNumber: number || '' }
        : { arrival: null, airport: null, called: null, altNumber: '' },
    }));
    try {
      const jd = reservation.json_data || reservation.detail?.json_data || { data: {} };
      const newJsonData = on ? withShuttleContact(jd, number) : withoutShuttleContact(jd);
      const saved = await saveReservationAndSync({
        ...reservation,
        json_data: newJsonData,
        detail: { ...(reservation.detail || {}), json_data: newJsonData },
      });
      const name = reservation.customer_name || '';
      setToast({
        open: true,
        message: on ? `${name} → ${number ? `別車両 ${number}` : AIRPORT_SHUTTLE}` : `${name} → 予約リスト（送迎を取り消し）`,
        severity: 'success',
      });
      if (on && onStaffClick) {
        setTimeout(() => onStaffClick(saved || reservation, 'driver', 'departure'), 300);
      }
    } catch (error) {
      console.error('Shuttle contact update failed:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
    }
    setTimeout(() => {
      setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
    }, 5000);
  }, [onStaffClick]);

  // ドロップオフ → ピックアップ への移動本体。返却予約は貸出済みにする。成功で true
  const moveDropoffToPickup = useCallback(async (vehicle: any, targetZoneId: string): Promise<boolean> => {
    const vid = String(vehicle.id);
    const pickupZone = ZONES.find(z => z.id === targetZoneId);
    const pickupStatusId = pickupZone?.statusCode ? statusIdByCode[pickupZone.statusCode] : undefined;
    if (typeof pickupStatusId !== 'number') {
      setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
      return false;
    }
    const pickupLabel = (pickupZone?.label || '').replace(/\n/g, '');
    let ok = true;
    setOptimisticStatus(prev => ({ ...prev, [vid]: pickupStatusId }));
    try {
      await saveVehicleAndSync(
        { id: vehicle.id, status: pickupStatusId, location_status: zoneLocationId(targetZoneId) },
        userId,
      );
      // 返却予約を貸出済みへ（ドロップオフから出たので返却は完了している）
      const linkedReturn = findReturnToComplete(filteredReturns, vid);
      if (linkedReturn && String(linkedReturn.status) !== '2') {
        await saveReservationAndSync({ ...linkedReturn, status: 2, status_code: 'done' });
      }
      setToast({
        open: true,
        message: `${vehicle.vehicle_code} → ${pickupLabel}${linkedReturn ? '（返却: 貸出済み）' : ''}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Shuttle pickup move failed:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      ok = false;
    }
    setTimeout(() => {
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
    }, 5000);
    return ok;
  }, [statusIdByCode, zoneLocationId, userId, filteredReturns]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as any;
    setActiveVehicle(data?.vehicle || null);
    setActiveReservation(data?.reservation || null);
  }, []);

  // 予約カードのjson_data更新ヘルパー
  const updateReservationJsonField = useCallback(async (reservation: any, key: string, value: string | undefined) => {
    const jd = reservation.json_data || reservation.detail?.json_data || { data: {} };
    const newData = { ...(jd.data || {}) };
    if (value) { newData[key] = value; } else { delete newData[key]; }
    const newJsonData = { ...jd, data: newData };
    await saveReservationAndSync({
      ...reservation,
      json_data: newJsonData,
      detail: { ...(reservation.detail || {}), json_data: newJsonData },
    });
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveVehicle(null);
    setActiveReservation(null);
    const { active, over } = event;
    if (!over) return;

    const data = active.data.current as any;
    // ゾーン見出しの「ここに置く」帯（MAIN_DROP_SUFFIX）はゾーン本体と同じ扱い（2026-10-21）
    const rawOverId = String(over.id);
    const targetZoneId = rawOverId.endsWith(MAIN_DROP_SUFFIX) ? rawOverId.slice(0, -MAIN_DROP_SUFFIX.length) : rawOverId;

    // --- 車両カード同士のスワップ検出 ---
    if (targetZoneId.startsWith('vehicle-drop-') && data.vehicle) {
      const sourceVehicle = data.vehicle;
      const targetVehicle = (over.data?.current as any)?.vehicle;
      if (!targetVehicle || String(sourceVehicle.id) === String(targetVehicle.id)) return;

      const srcVid = String(sourceVehicle.id);
      const tgtVid = String(targetVehicle.id);

      // スワップ不可チェック: 当日の返却が完了していない車両
      const canSwapVehicle = (vid: string) => {
        const ret = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
        if (!ret) return true; // 当日返却予約なし → OK
        const st = String(ret.status);
        return st === '2'; // done → 返却完了 → OK
      };

      if (!canSwapVehicle(srcVid)) {
        setToast({ open: true, message: `${sourceVehicle.vehicle_code} はまだ返却が完了していないため入替できません`, severity: 'warning' });
        return;
      }
      if (!canSwapVehicle(tgtVid)) {
        setToast({ open: true, message: `${targetVehicle.vehicle_code} はまだ返却が完了していないため入替できません`, severity: 'warning' });
        return;
      }

      // スワップ対象の予約を取得（貸出予約 = これから使う予約）
      const srcRes = filteredDepartures.find((r: any) => String(r.vehicle_id) === srcVid);
      const tgtRes = filteredDepartures.find((r: any) => String(r.vehicle_id) === tgtVid);

      if (!srcRes && !tgtRes) {
        setToast({ open: true, message: '入替対象の予約がありません', severity: 'warning' });
        return;
      }

      setVehicleSwapConfirm({ sourceVehicle, targetVehicle, sourceRes: srcRes, targetRes: tgtRes });
      return;
    }

    // --- 予約カードのDnD ---
    if (data?.reservation) {
      const r = data.reservation;
      // ピックアップ系ゾーンへのドロップ
      const pickupTargets: Record<string, string> = { 'airport_1f': '未' };
      PICKUP_SUB_ZONES.forEach(sz => { pickupTargets[sz.id] = sz.airportValue; });
      const rid = String(r.id);

      // 予約リストの「連絡済み」サブエリアへ（こちらから電話連絡した印。2026-08-10 追加）
      if (targetZoneId === CALLED_DROP_ID) {
        setOptimisticResJson(prev => ({ ...prev, [rid]: { arrival: null, airport: AIRPORT_CALLED, called: AIRPORT_CALLED } }));
        try {
          const jd = r.json_data || r.detail?.json_data || { data: {} };
          // 「空港: 連絡済」に統合（2026-10-19）。到着連絡は入れない（ピックアップから戻した場合は外す）
          const newData = { ...(jd.data || {}), '空港': AIRPORT_CALLED };
          delete newData['到着連絡'];
          delete newData[LEGACY_CALLED_JSON_KEY];
          const newJsonData = { ...jd, data: newData };
          await saveReservationAndSync({
            ...r,
            json_data: newJsonData,
            detail: { ...(r.detail || {}), json_data: newJsonData },
          });
          setToast({ open: true, message: `${r.customer_name} → 連絡済み`, severity: 'success' });
        } catch {
          setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
          setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
        }
        setTimeout(() => { setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; }); }, 5000);
        return;
      }

      // 予約リストへ戻す（到着連絡・空港・連絡済みの印をすべて解除）
      if (targetZoneId === 'reservation_list') {
        // 楽観的更新: 即座に予約リストに移動
        setOptimisticResJson(prev => ({ ...prev, [rid]: { arrival: null, airport: null, called: null } }));
        try {
          const jd = r.json_data || r.detail?.json_data || { data: {} };
          const newData = { ...(jd.data || {}) };
          delete newData['到着連絡'];
          delete newData['空港'];
          delete newData[LEGACY_CALLED_JSON_KEY];
          const newJsonData = { ...jd, data: newData };
          await saveReservationAndSync({
            ...r,
            json_data: newJsonData,
            detail: { ...(r.detail || {}), json_data: newJsonData },
          });
          setToast({ open: true, message: `${r.customer_name} → 予約リスト`, severity: 'success' });
        } catch {
          setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
          setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
        }
        setTimeout(() => { setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; }); }, 5000);
        return;
      }

      const airportValue = pickupTargets[targetZoneId];
      if (airportValue !== undefined) {
        // 楽観的更新: 即座にピックアップ内サブゾーンに移動
        setOptimisticResJson(prev => ({ ...prev, [rid]: { arrival: '有り', airport: airportValue } }));
        try {
          const jd = r.json_data || r.detail?.json_data || { data: {} };
          const newData = { ...(jd.data || {}), '到着連絡': '有り', '空港': airportValue };
          const newJsonData = { ...jd, data: newData };
          await saveReservationAndSync({
            ...r,
            json_data: newJsonData,
            detail: { ...(r.detail || {}), json_data: newJsonData },
          });
          const label = airportValue === '未' ? '連絡あり' : airportValue;
          setToast({ open: true, message: `${r.customer_name} → ${label}`, severity: 'success' });
          // 送迎車/返却車 = 送迎の運転者が要る → 運転者モーダル（2026-09-19）
          if (SHUTTLE_AIRPORT_VALUES.has(airportValue) && onStaffClick) {
            setTimeout(() => onStaffClick(r, 'driver', 'departure'), 300);
          }
        } catch {
          setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
          setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; });
        }
        setTimeout(() => { setOptimisticResJson(prev => { const next = { ...prev }; delete next[rid]; return next; }); }, 5000);
      }
      return;
    }

    // --- 車両カードのDnD ---
    const vehicle = data?.vehicle;
    if (!vehicle) return;

    const vid = String(vehicle.id);

    // --- 返却送迎（送迎車モード）の解除（2026-08-24）---
    // ピックアップ/ドロップオフ以外へ動かしたらモード終了。以降は通常どおり
    // 本来の次回貸出予約がカードに戻る。
    const shuttleIds = shuttleIdsByVehicle[vid] || [];
    if (shuttleIds.length && !SHUTTLE_ZONE_IDS.has(targetZoneId)) {
      setOptimisticShuttle(prev => ({ ...prev, [vid]: [] }));
      try {
        await saveVehicleAndSync({ id: vehicle.id, json_data: buildShuttleJsonData(vehicle, []) }, userId);
      } catch (error) {
        console.error('Shuttle release failed:', error);
        setOptimisticShuttle(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticShuttle(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
    }

    // --- 出発案内（2026-10-21）---
    // ピックアップ（空港1F P.U・赤嶺駅P.U・下地島P.U）→ 店舗、または「出発案内」枠へ落とした車両に印を付ける。
    // 印の付いた車両を それ以外（店舗の本体・出発準備・ほかのゾーン・貸出）へ動かしたら印を外す
    const guideSourceZoneId = statusToZone[Number(vehicle.status)];
    const toGuide = targetZoneId === GUIDE_DROP_ID
      || (targetZoneId === 'airport' && !!guideSourceZoneId && PICKUP_ZONE_IDS.has(guideSourceZoneId));
    const hadGuide = isGuideVehicle(vehicle);
    // 車両 json_data の土台。上で返却送迎を外した場合はその結果を使う（古い車両データで返却送迎を書き戻さない）
    const baseVehicleJson = (shuttleIds.length && !SHUTTLE_ZONE_IDS.has(targetZoneId))
      ? buildShuttleJsonData(vehicle, [])
      : vehicle.json_data;
    if (toGuide) {
      const shopStatusId = statusIdByCode['airport_store'];
      if (typeof shopStatusId !== 'number') {
        setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
        return;
      }
      if (hadGuide && Number(vehicle.status) === shopStatusId) return; // すでに出発案内
      setOptimisticStatus(prev => ({ ...prev, [vid]: shopStatusId }));
      setOptimisticGuide(prev => ({ ...prev, [vid]: true }));
      try {
        await saveVehicleAndSync({
          id: vehicle.id,
          status: shopStatusId,
          location_status: null, // 店舗は場所のエリアなので移動ステータスはクリア（2026-10-24）
          json_data: buildGuideJsonData(baseVehicleJson, true),
        }, userId);
        setToast({ open: true, message: `${vehicle.vehicle_code} → 出発案内`, severity: 'success' });
      } catch (error) {
        console.error('Guide move failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
        setOptimisticGuide(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
        setOptimisticGuide(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
      return;
    }
    if (hadGuide) {
      setOptimisticGuide(prev => ({ ...prev, [vid]: false }));
      try {
        await saveVehicleAndSync({ id: vehicle.id, json_data: buildGuideJsonData(baseVehicleJson, false) }, userId);
      } catch (error) {
        console.error('Guide release failed:', error);
        setOptimisticGuide(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticGuide(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
    }

    // --- ドロップオフ → ピックアップ = 返却送迎（送迎車モード）開始（2026-08-24）---
    // 返却客を降ろした車がそのまま別の客を迎えに行く運用。
    // 紐づく返却予約は確認モーダルなしで貸出済み(done)にし、送迎先の予約を選ばせる。
    const shuttleSourceZoneId = statusToZone[Number(vehicle.status)];
    if (
      PICKUP_ZONE_IDS.has(targetZoneId) &&
      shuttleSourceZoneId &&
      DROPOFF_ZONE_IDS.has(shuttleSourceZoneId)
    ) {
      // 既に送迎車モードなら聞き直さない（ピックアップ⇄ドロップオフの往復）。
      // それ以外は先に聞き、答えてから動かす（キャンセルなら車両も返却予約もそのまま。2026-11-04）
      if (shuttleIds.length) {
        await moveDropoffToPickup(vehicle, targetZoneId);
      } else {
        setShuttleSelected([]);
        setShuttleAssign({ vehicle, targetZoneId });
      }
      return;
    }

    // 本日貸出済み(H)へドロップ → 車両=departing + 紐づく貸出予約=in_use(3)
    // 店舗の「直接来店」サブエリアへドロップ（2026-08-10 追加）
    //   紐づく本日の貸出予約を「到着連絡=有り / 空港=直接来店」にする。
    //   これにより予約リストのカードもピックアップの「直接来店」へ移動する（表示は予約の値が正）。
    //   車両が店舗以外にいる場合は店舗ステータスへも移す。
    if (targetZoneId === PREPARE_DROP_ID) {
      const linkedDeparture = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
      if (!linkedDeparture) {
        setToast({ open: true, message: '本日の貸出予約が紐づいていないため出発準備にできません', severity: 'warning' });
        return;
      }
      // 受け方（送迎車/返却車/直接来店）を聞いてから applyPrepareContact で反映する
      setPrepareAssign({ vehicle, reservation: linkedDeparture });
      return;
    }

    if (targetZoneId === 'departed_today') {
      const departingStatusId = statusIdByCode['departing'];
      if (typeof departingStatusId !== 'number') {
        setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
        return;
      }
      setOptimisticStatus(prev => ({ ...prev, [vid]: departingStatusId }));
      setOptimisticDeparted(prev => new Set(prev).add(vid));
      try {
        await saveVehicleAndSync({
          id: vehicle.id,
          status: departingStatusId,
          location_status: null,
          ...buildClearedChecks(vehicle), // 出発したのでチェックを初期化（2026-09-05）
        }, userId);
        // 紐づく本日の貸出予約を in_use(3) に更新
        const linkedDeparture = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedDeparture) {
          await saveReservationAndSync({ ...linkedDeparture, status: 3, status_code: 'in_use' });
        }
        setToast({ open: true, message: `${vehicle.vehicle_code} → 出発中（予約: 利用中）`, severity: 'success' });
      } catch (error) {
        console.error('Departed today update failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
        setOptimisticDeparted(prev => { const next = new Set(prev); next.delete(vid); return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
        setOptimisticDeparted(prev => { const next = new Set(prev); next.delete(vid); return next; });
      }, 5000);
      return;
    }

    // 利用中(E)へドロップ → 車両=出発中 + 移動ステータスをクリア + 紐づく貸出予約=in_use(3)
    // 「貸出(H)」へ落とすのと同じ意味（お客様が乗って出発した）なので結果を揃える（2026-08-25）。
    // H との違いは置き場所だけ（返却予約が残っていれば E、無ければ H に並ぶ）。
    if (targetZoneId === 'in_use') {
      const departingStatusId = statusIdByCode['departing'];
      if (typeof departingStatusId !== 'number') {
        setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
        return;
      }
      // チェックが残っていれば、ステータスが既に出発中でも初期化のために保存する
      const needVehicleUpdate = Number(vehicle.status) !== departingStatusId
        || vehicle.location_status != null
        || hasCompletedChecks(vehicle);
      const linkedDeparture = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
      const needResUpdate = !!linkedDeparture
        && String(linkedDeparture.status) !== '3'
        && linkedDeparture.status !== 'in_use';
      if (!needVehicleUpdate && !needResUpdate) return; // 何も変わらないなら何もしない

      if (needVehicleUpdate) setOptimisticStatus(prev => ({ ...prev, [vid]: departingStatusId }));
      try {
        if (needVehicleUpdate) {
          await saveVehicleAndSync({
            id: vehicle.id,
            status: departingStatusId,
            location_status: null,
            ...buildClearedChecks(vehicle), // 出発したのでチェックを初期化（2026-09-05）
          }, userId);
        }
        if (needResUpdate) {
          await saveReservationAndSync({ ...linkedDeparture, status: 3, status_code: 'in_use' });
        }
        setToast({
          open: true,
          message: `${vehicle.vehicle_code} → 出発中${needResUpdate ? '（予約: 利用中）' : ''}`,
          severity: 'success',
        });
      } catch (error) {
        console.error('In-use drop update failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
      return;
    }

    // 空港ドロップオフへドロップ → 車両=送り中(d_o) + location_status + 返却運転者モーダル
    // 返却予約は **利用中のまま** にする。貸出済み(done)化はドロップオフから出た時に行う
    //（2026-08-24 変更。以前はここで即 done にしていたため、ドロップオフ→他ゾーンの
    //  確認モーダルが実質発火しなかった）
    if (targetZoneId === 'sending') {
      const sendingStatusId = statusIdByCode['d_o'];
      if (typeof sendingStatusId !== 'number') {
        setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
        return;
      }
      const sendingZoneLabel = (ZONES.find(z => z.id === 'sending')?.label || '送り中').replace(/\n/g, '');
      setOptimisticStatus(prev => ({ ...prev, [vid]: sendingStatusId }));
      try {
        await saveVehicleAndSync({ id: vehicle.id, status: sendingStatusId, location_status: zoneLocationId('sending') }, userId);
        const linkedReturn = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedReturn && onStaffClick) {
          setTimeout(() => onStaffClick(linkedReturn, 'driver', 'return'), 300);
        }
        setToast({ open: true, message: `${vehicle.vehicle_code} → ${sendingZoneLabel}`, severity: 'success' });
      } catch (error) {
        console.error('Drop-off update failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
      return;
    }

    // 利用中(E, departing)→店舗(A, airport_store) → 確認モーダル
    if (targetZoneId === 'airport' && Number(vehicle.status) === statusIdByCode['departing']) {
      const shopStatusId = statusIdByCode['airport_store'];
      if (typeof shopStatusId !== 'number') {
        setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
        return;
      }
      const linkedReturn = findReturnToComplete(filteredReturns, vid);
      // まず車両ステータスだけ店舗に変更
      setOptimisticStatus(prev => ({ ...prev, [vid]: shopStatusId }));
      try {
        await saveVehicleAndSync({ id: vehicle.id, status: shopStatusId, location_status: zoneLocationId('airport') }, userId);
        if (linkedReturn) {
          setDoneConfirm({ vehicle, returnReservation: linkedReturn });
        } else {
          setToast({ open: true, message: `${vehicle.vehicle_code} → 店舗`, severity: 'success' });
        }
      } catch (error) {
        console.error('Airport return failed:', error);
        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }
      setTimeout(() => {
        setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
      }, 5000);
      return;
    }

    const targetZone = ZONES.find(z => z.id === targetZoneId);
    if (!targetZone || !targetZone.statusCode) return;

    const targetStatusId = statusIdByCode[targetZone.statusCode];
    if (typeof targetStatusId !== 'number') {
      console.error(`Vehicle status not found for code: ${targetZone.statusCode}`);
      setToast({ open: true, message: 'ステータス情報が未取得です', severity: 'error' });
      return;
    }

    if (Number(vehicle.status) === targetStatusId) return;

    setOptimisticStatus(prev => ({ ...prev, [vid]: targetStatusId }));

    try {
      // 移動ステータスはエリアの対応表どおり（config/vehicleZones.ts zoneLocationStatus）。
      // 店舗・ヤード等の「場所」のエリアは対応表に無い＝クリア。P.U / D.O は 行き（→空港 等）を付ける（2026-10-24）
      const locStatus = zoneLocationId(targetZoneId);
      await saveVehicleAndSync({ id: vehicle.id, status: targetStatusId, location_status: locStatus }, userId);
      setToast({ open: true, message: `${vehicle.vehicle_code} → ${targetZone.label}`, severity: 'success' });

      // ピックアップ系へドロップ → 貸出予約の運転者モーダル
      // ドロップオフ系へドロップ → 返却予約の運転者モーダル
      // 2026-08-25: 'pickup'/'sending' 決め打ちをやめ、ゾーン集合で判定するようにした
      //（赤嶺駅 P.U/D.O・下地島 P.U/D.O でも同じように運転者を聞く）
      if (onStaffClick && PICKUP_ZONE_IDS.has(targetZoneId)) {
        const linkedDeparture = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedDeparture) {
          setTimeout(() => onStaffClick(linkedDeparture, 'driver', 'departure'), 300);
        }
      }
      if (onStaffClick && DROPOFF_ZONE_IDS.has(targetZoneId)) {
        const linkedReturn = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
        if (linkedReturn) {
          setTimeout(() => onStaffClick(linkedReturn, 'driver', 'return'), 300);
        }
      }

      // ドロップオフ(sending / akamine_south_do) → 非ドロップオフ への移動:
      // 紐づく返却予約があれば「貸出済みに変更しますか？」モーダルを表示
      const sourceZoneId = statusToZone[Number(vehicle.status)];
      if (
        sourceZoneId &&
        DROPOFF_ZONE_IDS.has(sourceZoneId) &&
        !DROPOFF_ZONE_IDS.has(targetZoneId)
      ) {
        const linkedReturn = findReturnToComplete(filteredReturns, vid);
        if (linkedReturn) {
          setDoneConfirm({ vehicle, returnReservation: linkedReturn, noToastOnDecline: true });
        }
      }
    } catch (error) {
      console.error('Vehicle status update failed:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
    }
    setTimeout(() => {
      setOptimisticStatus(prev => { const next = { ...prev }; delete next[vid]; return next; });
    }, 5000);
  }, [userId, updateReservationJsonField, filteredDepartures, filteredReturns, onStaffClick, statusIdByCode, statusToZone, shuttleIdsByVehicle, zoneLocationId, isGuideVehicle, moveDropoffToPickup]);

  // --- 返却送迎: 送迎先の候補予約（本日の未出発 booked。到着連絡済みを上に）---
  const shuttleCandidates = useMemo(() => {
    if (!shuttleAssign) return [] as any[];
    const getAirportValue = (r: any) => {
      const opt = optimisticResJson[String(r.id)];
      if (opt !== undefined) return opt.airport || '';
      return (r.json_data || r.detail?.json_data)?.data?.['空港'] || '';
    };
    const hasArrived = (r: any) => {
      const opt = optimisticResJson[String(r.id)];
      if (opt !== undefined) return opt.arrival === '有り';
      return (r.json_data || r.detail?.json_data)?.data?.['到着連絡'] === '有り';
    };
    return filteredDepartures
      .filter((r: any) => {
        const st = String(r.status).toLowerCase();
        return st === '1' || st === 'booked';
      })
      .map((r: any) => ({ res: r, arrived: hasArrived(r), airport: getAirportValue(r) }))
      .sort((a, b) => {
        if (a.arrived !== b.arrived) return a.arrived ? -1 : 1; // 到着連絡済みを先頭へ
        return dayjs(a.res.start_date).unix() - dayjs(b.res.start_date).unix();
      });
  }, [shuttleAssign, filteredDepartures, optimisticResJson]);

  // --- 返却送迎: 選択した予約を車両へ一時的に紐づける ---
  const confirmShuttleAssign = useCallback(async () => {
    const target = shuttleAssign;
    const ids = shuttleSelected;
    setShuttleAssign(null);
    if (!target || !ids.length) return;
    const v = target.vehicle;
    const vid = String(v.id);
    // 先に車両をピックアップへ動かす（失敗したら紐づけない）
    if (!(await moveDropoffToPickup(v, target.targetZoneId))) return;
    const picked = ids
      .map(id => filteredDepartures.find((r: any) => String(r.id) === id))
      .filter(Boolean);

    setOptimisticShuttle(prev => ({ ...prev, [vid]: ids }));
    setOptimisticResJson(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = { arrival: '有り', airport: SHUTTLE_AIRPORT_VALUE }; });
      return next;
    });
    try {
      await saveVehicleAndSync({ id: v.id, json_data: buildShuttleJsonData(v, ids) }, userId);
      // 送迎される予約は「到着連絡=有り / 空港=返却車」にしてピックアップの返却車エリアへ
      for (const r of picked) {
        const jd = (r as any).json_data || (r as any).detail?.json_data || { data: {} };
        const newJsonData = {
          ...jd,
          data: { ...(jd.data || {}), '到着連絡': '有り', '空港': SHUTTLE_AIRPORT_VALUE },
        };
        await saveReservationAndSync({
          ...(r as any),
          json_data: newJsonData,
          detail: { ...((r as any).detail || {}), json_data: newJsonData },
        });
      }
      const names = picked.map((r: any) => r.customer_name || '').filter(Boolean).join('、');
      setToast({ open: true, message: `${v.vehicle_code} 返却送迎: ${names || `${ids.length}件`}`, severity: 'success' });
      // 送迎する予約の貸出運転者を続けて指定（先頭1件）
      if (onStaffClick && picked[0]) {
        setTimeout(() => onStaffClick(picked[0], 'driver', 'departure'), 300);
      }
    } catch (error) {
      console.error('Shuttle assign failed:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
      setOptimisticShuttle(prev => { const next = { ...prev }; delete next[vid]; return next; });
      setOptimisticResJson(prev => {
        const next = { ...prev };
        ids.forEach(id => { delete next[id]; });
        return next;
      });
    }
    setTimeout(() => {
      setOptimisticShuttle(prev => { const next = { ...prev }; delete next[vid]; return next; });
      setOptimisticResJson(prev => {
        const next = { ...prev };
        ids.forEach(id => { delete next[id]; });
        return next;
      });
    }, 5000);
  }, [shuttleAssign, shuttleSelected, filteredDepartures, userId, onStaffClick, moveDropoffToPickup]);

  return (
    <>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* メインマップ */}
        {isMobile ? (
          // --- モバイル: 縦並びレイアウト ---
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
            {MOBILE_ZONE_ORDER.map(zoneId => {
              const zone = ZONES.find(z => z.id === zoneId);
              if (!zone) return null;
              const isSubGroupZone = zone.id === 'airport' || zone.id === 'yard';
              const sg = zone.id === 'airport' ? airportSubGroups : zone.id === 'yard' ? yardSubGroups : undefined;
              return (
                <DropZone
                  key={zone.id}
                  zone={{ ...zone, gridArea: '' }}
                  count={vehiclesByZone[zone.id]?.length || 0}
                  subGroups={isSubGroupZone ? sg : undefined}
                  renderCard={isSubGroupZone ? renderCard : undefined}
                  collapsibleLast={zone.id === 'yard'}
                  expandTick={expandTick}
                  fillBody={zone.id === 'in_use'}
                  flexChildren={zone.id === 'airport_1f'}
                >
                  {!isSubGroupZone && zone.id === 'airport_1f' && (() => {
                    const getAirportValue = (r: any) => {
                      const opt = optimisticResJson[String(r.id)];
                      if (opt !== undefined) return opt.airport || '未';
                      const jd = r.json_data || r.detail?.json_data;
                      return jd?.data?.['空港'] || '未';
                    };
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minHeight: 0 }}>
                        {PICKUP_SUB_ZONES.map(sz => {
                          const filtered = arrivedReservations.filter((r: any) => getAirportValue(r) === sz.airportValue);
                          return (
                            <PickupSubZone key={sz.id} id={sz.id} label={sz.label} reservations={filtered}
                              onReservationClick={(r) => setSelectedReservation(r)} borderColor={sz.borderColor} />
                          );
                        })}
                      </Box>
                    );
                  })()}
                  {!isSubGroupZone && zone.id !== 'airport_1f' && (vehiclesByZone[zone.id] || []).map((v: any) => (
                    <DraggableVehicleCard
                      key={v.id}
                      vehicle={v}
                      resInfo={reservationInfoMap[String(v.id)]}
                      onClick={() => setSelectedVehicleForModal(v)}
                      onLocationClear={handleLocationClear}
                      onToggleLocation={canToggleLocation(v) ? () => handleLocationClear(v) : undefined}
                      locStatusMap={locStatusMap}
                      lineUserMap={lineUserMap}
                      onOpenLineChat={onOpenLineChat}
                      highlight={highlightVehicleId === String(v.id)}
                    />
                  ))}
                </DropZone>
              );
            })}

            {/* H: 貸出 */}
            <DropZone zone={{ ...H_ZONE, gridArea: '' }} count={departedTodayVehicles.length + optimisticDeparted.size}>
              {[...departedTodayVehicles, ...Object.values(vehicles).filter((v: any) => optimisticDeparted.has(String(v.id)) && !departedTodayVehicles.some((d: any) => d.id === v.id))].map((v: any) => (
                <DraggableVehicleCard
                  key={v.id}
                  vehicle={v}
                  resInfo={reservationInfoMap[String(v.id)]}
                  onClick={() => setSelectedVehicleForModal(v)}
                  onLocationClear={handleLocationClear}
                  locStatusMap={locStatusMap}
                  lineUserMap={lineUserMap}
                  onOpenLineChat={onOpenLineChat}
                  highlight={highlightVehicleId === String(v.id)}
                />
              ))}
            </DropZone>

            {/* 区切り */}
            <Box sx={{ borderTop: '2px dashed #ccc', my: 1 }} />

            {/* 予約リスト */}
            <ReservationListZone
              count={waitingReservations.length}
              calledArea={
                <CalledSubZone count={calledReservations.length}>
                  {calledReservations.map((r: any) => renderReservationCard(r))}
                </CalledSubZone>
              }
            >
              {waitingReservations.map((r: any) => {
                const truncName = (r.customer_name || '').length > 6
                  ? (r.customer_name || '').slice(0, 6) + '...' : (r.customer_name || '');
                return (
                  <DraggableReservationCard key={r.id} reservation={r} onClick={() => setSelectedReservation(r)}>
                    <Typography sx={{ fontSize: '0.55rem', color: '#666', lineHeight: 1.2 }}>
                      {dayjs(r.start_date).format('HH:mm')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, lineHeight: 1.2 }}>
                      {truncName}
                    </Typography>
                  </DraggableReservationCard>
                );
              })}
              {waitingReservations.length === 0 && (
                <Typography sx={{ fontSize: '0.5rem', color: '#999', opacity: 0.6 }}>なし</Typography>
              )}
            </ReservationListZone>
          </Box>
        ) : (
          // --- PC: グリッドレイアウト ---
          <Box sx={{
            display: 'grid',
            gap: 1,
            gridTemplateAreas: ZONES_CONFIG.grid.areas,
            gridTemplateColumns: ZONES_CONFIG.grid.columns,
            gridTemplateRows: ZONES_CONFIG.grid.rows,
            minHeight: 200,
            p: 1,
          }}>
            {ZONES.map(zone => {
              const isSubGroupZone = zone.id === 'airport' || zone.id === 'yard';
              const sg = zone.id === 'airport' ? airportSubGroups : zone.id === 'yard' ? yardSubGroups : undefined;
              const zoneArrows =
                zone.id === 'airport' ? [
                  { position: 'left' as const, label: '←' },
                  { position: 'right' as const, label: '←' },
                  { position: 'bottom' as const, label: '↑↓' },
                ] :
                  zone.id === 'sending' ? [
                    { position: 'right' as const, label: '←' },
                  ] :
                    zone.id === 'pickup' ? [
                      { position: 'top' as const, label: '↓' },
                    ] :
                      (zone.id === 'akamine_south_pu' || zone.id === 'shimoji_pu') ? [
                        { position: 'right' as const, label: '↓↑' },
                      ] : undefined;
              return (
                <DropZone
                  key={zone.id}
                  zone={zone}
                  count={vehiclesByZone[zone.id]?.length || 0}
                  subGroups={isSubGroupZone ? sg : undefined}
                  renderCard={isSubGroupZone ? renderCard : undefined}
                  collapsibleLast={zone.id === 'yard'}
                  arrows={zoneArrows}
                  expandTick={expandTick}
                  fillBody={zone.id === 'in_use'}
                  flexChildren={zone.id === 'airport_1f'}
                >
                  {!isSubGroupZone && zone.id === 'airport_1f' && (() => {
                    // ピックアップ内サブゾーン分け（楽観的更新を優先参照）
                    const getAirportValue = (r: any) => {
                      const opt = optimisticResJson[String(r.id)];
                      if (opt !== undefined) return opt.airport || '未';
                      const jd = r.json_data || r.detail?.json_data;
                      return jd?.data?.['空港'] || '未';
                    };
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minHeight: 0 }}>
                        {PICKUP_SUB_ZONES.map(sz => {
                          const filtered = arrivedReservations.filter((r: any) => getAirportValue(r) === sz.airportValue);
                          return (
                            <PickupSubZone key={sz.id} id={sz.id} label={sz.label} reservations={filtered}
                              onReservationClick={(r) => setSelectedReservation(r)} borderColor={sz.borderColor} />
                          );
                        })}
                      </Box>
                    );
                  })()}
                  {!isSubGroupZone && zone.id !== 'airport_1f' && (vehiclesByZone[zone.id] || []).map((v: any) => (
                    <DraggableVehicleCard
                      key={v.id}
                      vehicle={v}
                      resInfo={reservationInfoMap[String(v.id)]}
                      onClick={() => setSelectedVehicleForModal(v)}
                      onLocationClear={handleLocationClear}
                      onToggleLocation={canToggleLocation(v) ? () => handleLocationClear(v) : undefined}
                      locStatusMap={locStatusMap}
                      lineUserMap={lineUserMap}
                      onOpenLineChat={onOpenLineChat}
                      highlight={highlightVehicleId === String(v.id)}
                    />
                  ))}
                </DropZone>
              );
            })}

            {/* R: 予約リスト（本日のbooked予約、ドロップで戻し可能） */}
            <ReservationListZone
              count={waitingReservations.length}
              calledArea={
                <CalledSubZone count={calledReservations.length}>
                  {calledReservations.map((r: any) => renderReservationCard(r))}
                </CalledSubZone>
              }
            >
              {waitingReservations.map((r: any) => {
                const truncName = (r.customer_name || '').length > 6
                  ? (r.customer_name || '').slice(0, 6) + '...' : (r.customer_name || '');
                return (
                  <DraggableReservationCard key={r.id} reservation={r} onClick={() => setSelectedReservation(r)}>
                    <Typography sx={{ fontSize: '0.55rem', color: '#666', lineHeight: 1.2 }}>
                      {dayjs(r.start_date).format('HH:mm')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, lineHeight: 1.2 }}>
                      {truncName}
                    </Typography>
                  </DraggableReservationCard>
                );
              })}
              {waitingReservations.length === 0 && (
                <Typography sx={{ fontSize: '0.5rem', color: '#999', opacity: 0.6 }}>なし</Typography>
              )}
            </ReservationListZone>

          </Box>
        )}

        {/* H: 貸出（常に表示、モバイル時はモバイルレイアウト内に含まれるのでPC版のみ） */}
        {!isMobile && (
          <Box sx={{ mt: 0.5, p: 1, minHeight: 120 }}>
            <DropZone zone={H_ZONE} count={departedTodayVehicles.length + optimisticDeparted.size}>
              {[...departedTodayVehicles, ...Object.values(vehicles).filter((v: any) => optimisticDeparted.has(String(v.id)) && !departedTodayVehicles.some((d: any) => d.id === v.id))].map((v: any) => (
                <DraggableVehicleCard
                  key={v.id}
                  vehicle={v}
                  resInfo={reservationInfoMap[String(v.id)]}
                  onClick={() => setSelectedVehicleForModal(v)}
                  onLocationClear={handleLocationClear}
                  locStatusMap={locStatusMap}
                  lineUserMap={lineUserMap}
                  onOpenLineChat={onOpenLineChat}
                  highlight={highlightVehicleId === String(v.id)}
                />
              ))}
            </DropZone>
          </Box>
        )}

        <DragOverlay>
          {activeVehicle && (
            <VehicleCard
              vehicle={activeVehicle}
              resInfo={reservationInfoMap[String(activeVehicle.id)]}
              isDragging
              locStatusMap={locStatusMap}
            />
          )}
          {activeReservation && (
            <Paper elevation={8} sx={{
              px: 0.6, py: 0.3, borderRadius: 1,
              backgroundColor: '#fff', border: '1.5px solid #00695c',
              transform: 'scale(1.05)', cursor: 'grabbing',
            }}>
              <Typography sx={{ fontSize: '0.55rem', color: '#666', lineHeight: 1.2 }}>
                {dayjs(activeReservation.start_date).format('HH:mm')}
              </Typography>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, lineHeight: 1.2 }}>
                {(activeReservation.customer_name || '').length > 6
                  ? (activeReservation.customer_name || '').slice(0, 6) + '...'
                  : (activeReservation.customer_name || '')}
              </Typography>
            </Paper>
          )}
        </DragOverlay>
      </DndContext>

      {/* 車両スワップ確認Dialog */}
      <Dialog open={Boolean(vehicleSwapConfirm)} onClose={() => setVehicleSwapConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 1 }}>車両の入替確認</DialogTitle>
        <DialogContent>
          {vehicleSwapConfirm && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                <Typography variant="body2" fontWeight={700}>
                  {vehicleSwapConfirm.sourceRes?.customer_name || '予約なし'}
                </Typography>
                <Typography variant="h6" fontWeight={800} color="primary">
                  {vehicleSwapConfirm.sourceVehicle.vehicle_code}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ color: '#94a3b8' }}>⇄</Typography>
              <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                <Typography variant="body2" fontWeight={700}>
                  {vehicleSwapConfirm.targetRes?.customer_name || '予約なし'}
                </Typography>
                <Typography variant="h6" fontWeight={800} color="primary">
                  {vehicleSwapConfirm.targetVehicle.vehicle_code}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleSwapConfirm(null)}>キャンセル</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!vehicleSwapConfirm) return;
              const { sourceVehicle, targetVehicle, sourceRes, targetRes } = vehicleSwapConfirm;
              setVehicleSwapConfirm(null);
              try {
                const saves: Promise<any>[] = [];
                if (sourceRes) saves.push(saveReservationAndSync({ ...sourceRes, vehicle_id: String(targetVehicle.id) }));
                if (targetRes) saves.push(saveReservationAndSync({ ...targetRes, vehicle_id: String(sourceVehicle.id) }));
                await Promise.all(saves);
                setToast({ open: true, message: '車両を入替しました', severity: 'success' });
              } catch (err) {
                console.error('Vehicle swap failed:', err);
                setToast({ open: true, message: '車両の入替に失敗しました', severity: 'error' });
              }
            }}
          >
            入替する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* 予約モーダル */}
      <Dialog
        open={!!selectedReservation}
        onClose={() => setSelectedReservation(null)}
        maxWidth="xs"
        fullWidth
      >
        {selectedReservation && (() => {
          const r = selectedReservation;
          const jsonData = r.json_data || r.detail?.json_data;
          const hasArrival = jsonData?.data?.['到着連絡'] === '有り';
          const vehicleObj = Object.values(vehicles).find((veh: any) => String(veh.id) === String(r.vehicle_id));
          return (
            <>
              <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>
                {r.customer_name}
              </DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    貸出: {dayjs(r.start_date).format('HH:mm')}
                  </Typography>
                  {vehicleObj && (
                    <Typography variant="body2" color="text.secondary">
                      車両: {(vehicleObj as any).vehicle_code}
                    </Typography>
                  )}
                  <Button
                    variant={hasArrival ? 'contained' : 'outlined'}
                    color={hasArrival ? 'success' : 'inherit'}
                    fullWidth
                    disabled={isSavingArrival}
                    startIcon={isSavingArrival ? <CircularProgress size={16} color="inherit" /> : undefined}
                    onClick={async () => {
                      setIsSavingArrival(true);
                      try {
                        const currentJsonData = jsonData || { data: {} };
                        const newValue = hasArrival ? undefined : '有り';
                        const newData = { ...(currentJsonData.data || {}) };
                        if (newValue) {
                          newData['到着連絡'] = newValue;
                          // 「連絡済」（こちらから電話した印）からの到着連絡は「未」から始める（2026-10-19）
                          if (!newData['空港'] || newData['空港'] === AIRPORT_CALLED) newData['空港'] = '未';
                        } else {
                          delete newData['到着連絡'];
                          delete newData['空港'];
                        }
                        const newJsonData = { ...currentJsonData, data: newData };
                        await saveReservationAndSync({
                          ...r,
                          json_data: newJsonData,
                          detail: { ...(r.detail || {}), json_data: newJsonData },
                        });
                        setToast({
                          open: true,
                          message: hasArrival ? `${r.customer_name} 到着連絡を取消` : `${r.customer_name} 到着連絡を記録`,
                          severity: 'success',
                        });
                        setSelectedReservation(null);
                      } catch {
                        setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                      } finally {
                        setIsSavingArrival(false);
                      }
                    }}
                    sx={{ mt: 1 }}
                  >
                    {hasArrival ? '到着連絡: ON（クリックで取消）' : '到着連絡'}
                  </Button>
                  {/* ピックアップサブゾーン選択 */}
                  {hasArrival && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {PICKUP_SUB_ZONES.filter(sz => sz.airportValue !== '未').map(sz => {
                        const currentAirport = jsonData?.data?.['空港'];
                        const isActive = currentAirport === sz.airportValue;
                        return (
                          <Button
                            key={sz.id}
                            variant={isActive ? 'contained' : 'outlined'}
                            size="small"
                            disabled={isSavingArrival}
                            onClick={async () => {
                              setIsSavingArrival(true);
                              try {
                                const currentJsonData = jsonData || { data: {} };
                                const newData = { ...(currentJsonData.data || {}), '空港': isActive ? '未' : sz.airportValue };
                                const newJsonData = { ...currentJsonData, data: newData };
                                await saveReservationAndSync({
                                  ...r,
                                  json_data: newJsonData,
                                  detail: { ...(r.detail || {}), json_data: newJsonData },
                                });
                                setToast({ open: true, message: `${r.customer_name} → ${sz.label}`, severity: 'success' });
                                setSelectedReservation(null);
                              } catch {
                                setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                              } finally {
                                setIsSavingArrival(false);
                              }
                            }}
                            sx={{
                              fontSize: '0.75rem',
                              ...(isActive ? { backgroundColor: sz.borderColor, color: (sz as any).textColor || '#fff', '&:hover': { backgroundColor: sz.borderColor, filter: 'brightness(0.9)' } } : { borderColor: sz.borderColor, color: sz.borderColor }),
                            }}
                          >
                            {sz.label}
                          </Button>
                        );
                      })}
                      {jsonData?.data?.['空港'] && jsonData.data['空港'] !== '未' && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="inherit"
                          disabled={isSavingArrival}
                          onClick={async () => {
                            setIsSavingArrival(true);
                            try {
                              const currentJsonData = jsonData || { data: {} };
                              const newData = { ...(currentJsonData.data || {}), '空港': '未' };
                              const newJsonData = { ...currentJsonData, data: newData };
                              await saveReservationAndSync({
                                ...r,
                                json_data: newJsonData,
                                detail: { ...(r.detail || {}), json_data: newJsonData },
                              });
                              setToast({ open: true, message: `${r.customer_name} → 連絡あり`, severity: 'success' });
                              setSelectedReservation(null);
                            } catch {
                              setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                            } finally {
                              setIsSavingArrival(false);
                            }
                          }}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          クリア
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedReservation(null)}>閉じる</Button>
                <Button variant="outlined" onClick={() => {
                  setSelectedReservation(null);
                  if (vehicleObj) onVehicleClick?.(vehicleObj);
                }}>
                  車両詳細
                </Button>
                <Button variant="outlined" onClick={() => {
                  setSelectedReservation(null);
                  onReservationClick?.(r);
                }}>
                  予約詳細
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* 出発準備モーダル: 店舗「出発準備」へ車両をドロップしたとき、お客様の受け方を選ぶ（2026-09-19） */}
      <Dialog open={!!prepareAssign} onClose={() => setPrepareAssign(null)} maxWidth="xs" fullWidth>
        {prepareAssign && (
          <>
            <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>
              {prepareAssign.vehicle.vehicle_code} を出発準備にします
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                貸出: {dayjs(prepareAssign.reservation.start_date).format('HH:mm')} {prepareAssign.reservation.customer_name} 様 — お客様の受け方を選んでください
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {PICKUP_SUB_ZONES.filter(sz => PREPARE_AIRPORT_VALUES.has(sz.airportValue)).map(sz => (
                  <Button
                    key={sz.id}
                    variant="outlined"
                    onClick={() => {
                      const { vehicle, reservation } = prepareAssign;
                      setPrepareAssign(null);
                      applyPrepareContact(vehicle, reservation, sz.airportValue);
                    }}
                    sx={{ justifyContent: 'flex-start', borderColor: sz.borderColor, color: sz.textColor ? undefined : sz.borderColor, fontWeight: 700 }}
                  >
                    {sz.label}
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400 }}>
                      {sz.airportValue === AIRPORT_WALKIN ? 'お客様が直接店舗へ' : 'お客様を運ぶ → 運転者を選びます'}
                    </Typography>
                  </Button>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrepareAssign(null)}>キャンセル</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 貸出済み確認モーダル（利用中→空港店移動時 / ドロップオフ→非ドロップオフ移動時） */}
      <Dialog open={!!doneConfirm} onClose={() => setDoneConfirm(null)} maxWidth="xs" fullWidth>
        {doneConfirm && (
          <>
            <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>
              {doneConfirm.returnReservation.customer_name} 様を貸出済みに変更しますか？
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                {doneConfirm.vehicle.vehicle_code}（返却: {dayjs(doneConfirm.returnReservation.end_date).format('HH:mm')}）
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                const noToast = doneConfirm.noToastOnDecline;
                const code = doneConfirm.vehicle.vehicle_code;
                setDoneConfirm(null);
                if (!noToast) {
                  setToast({ open: true, message: `${code} → 空港店`, severity: 'success' });
                }
              }}>
                いいえ
              </Button>
              <Button variant="contained" color="primary" onClick={async () => {
                const r = doneConfirm.returnReservation;
                const customerName = r.customer_name || '予約';
                try {
                  await saveReservationAndSync({ ...r, status: 2, status_code: 'done' });
                  setToast({ open: true, message: `${customerName} 様を貸出済みにしました`, severity: 'success' });
                } catch {
                  setToast({ open: true, message: '予約の更新に失敗しました', severity: 'error' });
                }
                setDoneConfirm(null);
              }}>
                はい、貸出済みにする
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 返却送迎（送迎車モード）: 送迎する予約の選択モーダル（2026-08-24） */}
      <Dialog
        open={!!shuttleAssign}
        onClose={() => setShuttleAssign(null)}
        maxWidth="xs"
        fullWidth
      >
        {shuttleAssign && (
          <>
            <DialogTitle sx={{ pb: 0.5, fontSize: '1rem' }}>
              {shuttleAssign.vehicle.vehicle_code} で送迎する予約
            </DialogTitle>
            <DialogContent sx={{ pb: 1 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#666', mb: 1 }}>
                返却のお客様を降ろした車がそのまま迎えに行く予約を選んでください。
                選んだ予約はピックアップの「返却車」エリアに移り、その予約の車両カードに返却車の番号が出ます。
              </Typography>
              {shuttleCandidates.length === 0 && (
                <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>
                  対象になる本日の貸出予約がありません
                </Typography>
              )}
              <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {shuttleCandidates.map(({ res, arrived, airport }) => {
                  const rid = String(res.id);
                  const checked = shuttleSelected.includes(rid);
                  return (
                    <Box
                      key={rid}
                      onClick={() => setShuttleSelected(prev => (
                        prev.includes(rid) ? prev.filter(x => x !== rid) : [...prev, rid]
                      ))}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.25,
                        cursor: 'pointer', borderRadius: 1,
                        backgroundColor: checked ? '#ede9fe' : 'transparent',
                        '&:hover': { backgroundColor: checked ? '#ddd6fe' : '#f5f5f5' },
                      }}
                    >
                      <Checkbox size="small" checked={checked} sx={{ p: 0.5 }} />
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, width: '3.2rem', flexShrink: 0 }}>
                        {dayjs(res.start_date).format('HH:mm')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', flex: 1, minWidth: 0 }} noWrap>
                        {res.customer_name || ''}
                      </Typography>
                      {/* その予約の車両（2026-11-04） */}
                      <Typography
                        data-testid="shuttle-candidate-vehicle"
                        title={vehicles[String(res.vehicle_id)]?.vehicle_code || ''}
                        sx={{ fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, color: res.vehicle_id ? 'text.primary' : 'text.disabled' }}
                      >
                        {res.vehicle_id ? shortVehicleCode(vehicles[String(res.vehicle_id)]?.vehicle_code) : '未割当'}
                      </Typography>
                      {arrived && (
                        <span style={{
                          fontSize: '0.6rem', fontWeight: 700, padding: '0 4px', borderRadius: '3px',
                          backgroundColor: '#00695c', color: '#fff', whiteSpace: 'nowrap',
                        }}>{airport || '到着'}</span>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShuttleAssign(null)}>キャンセル</Button>
              <Button onClick={() => {
                const target = shuttleAssign;
                setShuttleAssign(null);
                moveDropoffToPickup(target.vehicle, target.targetZoneId);
              }}>送迎なし</Button>
              <Button
                variant="contained"
                disabled={shuttleSelected.length === 0}
                onClick={confirmShuttleAssign}
              >
                返却送迎にする
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 「別車両」の車両番号（2026-11-04） */}
      <Dialog open={!!altShuttleAsk} onClose={() => setAltShuttleAsk(null)} maxWidth="xs" fullWidth>
        {altShuttleAsk && (() => {
          const submit = () => {
            const { reservation, number } = altShuttleAsk;
            if (!number) return;
            setAltShuttleAsk(null);
            applyShuttleContact(reservation, true, number);
          };
          return (
            <>
              <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>車両番号？</DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {altShuttleAsk.reservation.customer_name} 様を迎えに行く車の番号（数字）
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="例: 1234"
                  value={altShuttleAsk.number}
                  onChange={(e) => {
                    const number = normalizeAltShuttleNumber(e.target.value);
                    setAltShuttleAsk(prev => (prev ? { ...prev, number } : prev));
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) submit(); }}
                  slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': '車両番号' } }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAltShuttleAsk(null)}>キャンセル</Button>
                <Button variant="contained" disabled={!altShuttleAsk.number} onClick={submit}>決定</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* 車両カードクリックモーダル */}
      <Dialog open={!!selectedVehicleForModal} onClose={() => setSelectedVehicleForModal(null)} maxWidth="xs" fullWidth>
        {selectedVehicleForModal && (() => {
          const v = selectedVehicleForModal;
          const vid = String(v.id);
          const linkedDep = filteredDepartures.find((r: any) => String(r.vehicle_id) === vid);
          const linkedRet = filteredReturns.find((r: any) => String(r.vehicle_id) === vid);
          const linked = linkedDep || linkedRet;
          const listType = linkedDep ? 'departure' : 'return';
          return (
            <>
              <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>
                {v.vehicle_code}
                {v.vehicle_type?.vehicle_type_name && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {v.vehicle_type.vehicle_type_name}
                  </Typography>
                )}
              </DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                {linked && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {linkedDep ? '貸出' : '返却'}: {dayjs(linkedDep ? linked.start_date : linked.end_date).format('HH:mm')} {linked.customer_name}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {linked && onStaffClick && (
                    <Button variant="outlined" size="small" onClick={() => {
                      setSelectedVehicleForModal(null);
                      onStaffClick(linked, 'driver', listType);
                    }}>
                      運転者
                    </Button>
                  )}
                  <Button variant="outlined" size="small" onClick={() => {
                    setSelectedVehicleForModal(null);
                    onVehicleClick?.(v);
                  }}>
                    車両詳細
                  </Button>
                  {/* 予約詳細（2026-08-10 追加）。紐づく本日の貸出/返却予約の編集パネルを開く */}
                  {linked && onReservationClick && (
                    <Button variant="outlined" size="small" onClick={() => {
                      setSelectedVehicleForModal(null);
                      onReservationClick(linked);
                    }}>
                      予約詳細
                    </Button>
                  )}
                </Box>
                {/* 車両位置ステータス選択 */}
                <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #eee' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>車両位置</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {[
                      { id: 1, label: '→空港', color: '#1565c0' },
                      { id: 2, label: '→店舗', color: '#e65100' },
                      { id: 3, label: '→ヤード', color: '#4e342e' },
                    ].map(ls => {
                      const isActive = v.location_status === ls.id;
                      return (
                        <Button
                          key={ls.id}
                          variant={isActive ? 'contained' : 'outlined'}
                          size="small"
                          onClick={async () => {
                            try {
                              const newLocStatus = isActive ? null : ls.id;
                              await saveVehicleAndSync({ id: v.id, location_status: newLocStatus }, userId);
                              setToast({ open: true, message: `${v.vehicle_code} ${isActive ? '位置クリア' : ls.label}`, severity: 'success' });
                              setSelectedVehicleForModal(null);
                            } catch {
                              setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                            }
                          }}
                          sx={{
                            fontSize: '0.7rem',
                            ...(isActive ? { backgroundColor: ls.color, '&:hover': { backgroundColor: ls.color, filter: 'brightness(0.9)' } } : { borderColor: ls.color, color: ls.color }),
                          }}
                        >
                          {ls.label}
                        </Button>
                      );
                    })}
                    {v.location_status && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="inherit"
                        onClick={async () => {
                          try {
                            await saveVehicleAndSync({ id: v.id, location_status: null }, userId);
                            setToast({ open: true, message: `${v.vehicle_code} 位置クリア`, severity: 'success' });
                            setSelectedVehicleForModal(null);
                          } catch {
                            setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
                          }
                        }}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        クリア
                      </Button>
                    )}
                    {/* お客様を運ぶ車（2026-11-04）。予約カードは空港1Fの送迎車サブエリアへ。もう一度押すと取り消し */}
                    {linkedDep && hasShuttleSubZone && (() => {
                      const opt = optimisticResJson[String(linkedDep.id)];
                      const contact = opt !== undefined
                        ? { on: opt.arrival === ARRIVAL_CONTACT_ON, airport: String(opt.airport ?? '') }
                        : getArrivalContact(linkedDep);
                      const isShuttle = contact.on && contact.airport === AIRPORT_SHUTTLE;
                      const altNumber = isShuttle
                        ? (opt?.altNumber !== undefined ? opt.altNumber : getAltShuttleNumber(linkedDep))
                        : '';
                      const color = '#e65100';
                      const activeSx = { backgroundColor: color, '&:hover': { backgroundColor: color, filter: 'brightness(0.9)' } };
                      const idleSx = { borderColor: color, color };
                      return (
                        <>
                          <Button
                            variant={isShuttle && !altNumber ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => {
                              setSelectedVehicleForModal(null);
                              applyShuttleContact(linkedDep, !(isShuttle && !altNumber));
                            }}
                            sx={{ fontSize: '0.7rem', ...(isShuttle && !altNumber ? activeSx : idleSx) }}
                          >
                            送迎車
                          </Button>
                          <Button
                            variant={altNumber ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => {
                              setSelectedVehicleForModal(null);
                              if (altNumber) applyShuttleContact(linkedDep, false);
                              else setAltShuttleAsk({ vehicle: v, reservation: linkedDep, number: '' });
                            }}
                            sx={{ fontSize: '0.7rem', ...(altNumber ? activeSx : idleSx) }}
                          >
                            {altNumber ? `別${altNumber}` : '別車両'}
                          </Button>
                        </>
                      );
                    })()}
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSelectedVehicleForModal(null)}>閉じる</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </>
  );
}
